import { useState, useEffect, useMemo } from "react";
import { useNavigate, Routes, Route, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Users,
  Calendar,
  CalendarDays,
  Package,
  CreditCard,
  Settings,
  LogOut,
  Building2,
  Dumbbell,
  ChevronRight,
  ChevronDown,
  Bell,
  Menu,
  X,
  Plus,
  TrendingUp,
  Clock,
  MapPin,
  Map,
  Star,
  QrCode,
  DoorOpen,
  MessageCircle,
  MessageSquare,
  Activity,
  Crown,
  Key,
  Lock,
  ShieldCheck,
  AlertCircle,
  Trash2,
  Tag,
  Layers,
  UserCog,
  ClipboardList,
  History,
  BarChart,
  User,
  Banknote,
  FileText,
  Wallet,
  Gift,
  Receipt,
  Briefcase,
  Target,
  Trophy,
  ThumbsUp,
  Eye,
  Languages,
} from "lucide-react";
import { API } from "@/App";
import ReservationsModule from "@/components/ReservationsModule";
import MembersModule from "@/components/MembersModule";
import TrainersModule from "@/components/TrainersModule";
import EmployeesModule from "@/components/EmployeesModule";
import PackagesModule from "@/components/PackagesModule";
import PaymentsModule from "@/components/PaymentsModule";
import MeasurementsModule from "@/components/MeasurementsModule";
import QRScanModule from "@/components/QRScanModule";
import RoomsModule from "@/components/RoomsModule";
import MessagingModule from "@/components/MessagingModule";
import MemberReservationCalendar from "@/components/MemberReservationCalendar";
import MemberGroupClasses from "@/pages/MemberGroupClasses";
import TrainerCommissionReport from "@/components/TrainerCommissionReport";
import BranchManagement from "@/components/BranchManagement";
import TrainerCollectionsModule from "@/components/TrainerCollectionsModule";
import SettingsModule from "@/components/SettingsModule";
import RatingModule from "@/components/RatingModule";
import MyPackagesModule from "@/components/MyPackagesModule";
import MyMeasurementsModule from "@/components/MyMeasurementsModule";
import MyClassesModule from "@/components/MyClassesModule";
import MyStudentsModule from "@/components/MyStudentsModule";
import MyCommissionsModule from "@/components/MyCommissionsModule";
import MyFinancesModule from "@/components/MyFinancesModule";
import CommissionsModule from "@/components/CommissionsModule";
import NotificationBell from "@/components/NotificationBell";
import AnalyticsModule from "@/components/AnalyticsModule";
import SubscriptionModule, { UpgradePrompt } from "@/components/SubscriptionModule";
import AdminCodeGeneratorModule from "@/components/AdminCodeGeneratorModule";
import BusinessApprovalModule from "@/components/BusinessApprovalModule";
import AdminSettingsModule from "@/components/AdminSettingsModule";
import RequestsModule from "@/components/RequestsModule";
import ResetDatabaseModule from "@/components/ResetDatabaseModule";
import TranslationManagementModule from "@/components/TranslationManagementModule";
import PriceListModule from "@/components/PriceListModule";
import TrainerSellPackageModule from "@/components/TrainerSellPackageModule";
import TrainerPaymentsModule from "@/components/TrainerPaymentsModule";
import TrainerReservationsModule from "@/components/TrainerReservationsModule";
import MemberCreateRequest from "@/components/MemberCreateRequest";
import MemberQRCheckin from "@/components/MemberQRCheckin";
import UserProfileModule from "@/components/UserProfileModule";
import MemberRequestsModule from "@/components/MemberRequestsModule";
import AddressChangeRequestsModule from "@/components/AddressChangeRequestsModule";
import TrainerVisibilityRequestsModule from "@/components/TrainerVisibilityRequestsModule";
import SalaryPaymentsModule from "@/components/SalaryPaymentsModule";
import BonusPaymentsModule from "@/components/BonusPaymentsModule";
import BusinessExpensesModule from "@/components/BusinessExpensesModule";
import ReportsModule from "@/components/ReportsModule";
import BusinessSurveysModule from "@/components/BusinessSurveysModule";
import MySurveysModule from "@/components/MySurveysModule";
import EventsModule from "@/components/EventsModule";
import MyEventsModule from "@/components/MyEventsModule";
import EvaluationsModule from "@/components/EvaluationsModule";
import MyEvaluationsModule from "@/components/MyEvaluationsModule";
import GoalsModule from "@/components/GoalsModule";
import MemberFormsModule from "@/components/MemberFormsModule";
import AllPackagesModule from "@/components/AllPackagesModule";
import AuditLogModule from "@/components/AuditLogModule";
import MemberDetailPage from "@/pages/MemberDetailPage";
import TrainerDetailPage from "@/pages/TrainerDetailPage";
import EmployeeDetailPage from "@/pages/EmployeeDetailPage";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";

// Dashboard Overview Component
const DashboardOverview = ({ user, businesses, selectedBusiness, userPermissions }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['dashboard', 'common', "reports", "packages", "employees", "admin"]);
  const { formatPrice, currencySymbol } = useCurrency();
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();
  const [stats, setStats] = useState({
    members_count: 0,
    trainers_count: 0,
    today_reservations: 0,
    total_revenue: 0,
    upcoming_reservations: [],
  });
  const [showRevenueModal, setShowRevenueModal] = useState(false);
  const [revenueDetails, setRevenueDetails] = useState(null);
  const [loadingRevenue, setLoadingRevenue] = useState(false);

  // Permission check helper for this component
  const canAccessPage = (path) => {
    // Business owners always have access
    if (user?.role === "business") return true;
    // Check userPermissions
    const permMap = {
      "/dashboard/members": "members_list",
      "/dashboard/trainers": "trainers",
      "/dashboard/reservations": "reservations",
      "/dashboard/reports": "reports",
    };
    const permKey = permMap[path];
    if (!permKey) return true;
    return userPermissions?.permissions?.[permKey] === true;
  };

  // Check if user can view revenue
  const canViewRevenue = () => {
    if (user?.role === "business") return true;
    return userPermissions?.is_owner || 
           userPermissions?.is_co_owner ||
           userPermissions?.is_manager || 
           userPermissions?.permissions?.can_view_revenue === true;
  };

  const fetchStats = async (businessId) => {
    try {
      const response = await fetch(`${API}/businesses/${businessId}/stats`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        console.log("Stats data:", data);
        setStats(data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchRevenueDetails = async () => {
    if (!selectedBusiness?.business_id) return;
    setLoadingRevenue(true);
    try {
      const response = await fetch(`${API}/businesses/${selectedBusiness.business_id}/revenue-details`, {
        credentials: "include",
      });
      if (response.ok) {
        setRevenueDetails(await response.json());
      }
    } catch (error) {
      console.error("Error fetching revenue details:", error);
    } finally {
      setLoadingRevenue(false);
    }
  };

  useEffect(() => {
    if (selectedBusiness?.business_id) {
      fetchStats(selectedBusiness.business_id);
    }
  }, [selectedBusiness]);

  // Handle stat card clicks - check permissions first
  const handleStatClick = (statType) => {
    switch (statType) {
      case "members":
        if (canAccessPage("/dashboard/members")) {
          navigate("/dashboard/members");
        }
        break;
      case "trainers":
        if (canAccessPage("/dashboard/trainers")) {
          navigate("/dashboard/trainers");
        }
        break;
      case "reservations":
        if (canAccessPage("/dashboard/reservations")) {
          navigate("/dashboard/reservations");
        }
        break;
      case "revenue":
        // Revenue modal requires can_view_revenue permission or being business owner/manager
        if (canViewRevenue()) {
          setShowRevenueModal(true);
          fetchRevenueDetails();
        }
        break;
      default:
        break;
    }
  };

  // Check if stat card should be clickable
  const isStatClickable = (statType) => {
    if (user?.role === "business") return true;
    switch (statType) {
      case "members":
        return canAccessPage("/dashboard/members");
      case "trainers":
        return canAccessPage("/dashboard/trainers");
      case "reservations":
        return canAccessPage("/dashboard/reservations");
      case "revenue":
        return canViewRevenue();
      default:
        return true;
    }
  };

  // Check if stat card should be visible
  const isStatVisible = (statType) => {
    if (user?.role === "business") return true;
    switch (statType) {
      case "revenue":
        // Gelir kartı sadece işletme sahibi, yönetici veya can_view_revenue izni olanlar için görünür
        return canViewRevenue();
      default:
        return true;
    }
  };

  const statCards = [
    {
      title: t('dashboard:overview.totalMembers'),
      value: stats.members_count,
      icon: Users,
      color: "text-blue-500",
      bg: "bg-blue-50",
      onClick: () => handleStatClick("members"),
      clickable: isStatClickable("members"),
      visible: isStatVisible("members"),
    },
    {
      title: t('dashboard:overview.trainers'),
      value: stats.trainers_count,
      icon: Dumbbell,
      color: "text-green-500",
      bg: "bg-green-50",
      onClick: () => handleStatClick("trainers"),
      clickable: isStatClickable("trainers"),
      visible: isStatVisible("trainers"),
    },
    {
      title: t('dashboard:overview.todayLessons'),
      value: stats.today_reservations,
      icon: Calendar,
      color: "text-purple-500",
      bg: "bg-purple-50",
      onClick: () => handleStatClick("reservations"),
      clickable: isStatClickable("reservations"),
      visible: isStatVisible("reservations"),
    },
    {
      title: t('dashboard:overview.monthlyNetRevenue'),
      value: `${currencySymbol}${(stats.net_revenue || stats.total_revenue || 0).toLocaleString()}`,
      icon: TrendingUp,
      color: (stats.net_revenue || stats.total_revenue || 0) >= 0 ? "text-green-500" : "text-red-500",
      bg: (stats.net_revenue || stats.total_revenue || 0) >= 0 ? "bg-green-50" : "bg-red-50",
      onClick: () => handleStatClick("revenue"),
      clickable: isStatClickable("revenue"),
      visible: isStatVisible("revenue"),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Pending Business Banner */}
      {selectedBusiness?.approval_status === "pending" && (
        <Card className="border-2 border-yellow-300 bg-yellow-50">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-800">{t('dashboard:overview.pendingBanner')}</h3>
              <p className="text-sm text-yellow-700">
                İşletmeniz inceleme aşamasındadır. Onaylandığında haritada görünür olacak ve email ile bilgilendirileceksiniz.
              </p>
            </div>
            <Badge className="bg-yellow-200 text-yellow-800 border-yellow-300">
              <Clock className="w-4 h-4 mr-1" />
              {t('common:pending')}
            </Badge>
          </CardContent>
        </Card>
      )}

      {/* Rejected Business Banner */}
      {selectedBusiness?.approval_status === "rejected" && (
        <Card className="border-2 border-red-300 bg-red-50">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-red-800">{t('dashboard:overview.rejected')}</h3>
              <p className="text-sm text-red-700">
                {selectedBusiness.approval_note || t('dashboard:overview.rejectedNote')}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-navy">
            {t('dashboard:overview.hello')}, {user?.name}!
          </h1>
          <p className="text-grey mt-1">
            {user?.role === "trainer" ? t('dashboard:overview.checkSettings') : t('dashboard:overview.checkSettings')}
          </p>
        </div>
        {/* Only show "Yeni İşletme" if user is business owner and has no main business yet */}
        {user?.role === "business" && businesses.length === 0 && (
          <Button
            data-testid="add-business-btn"
            onClick={() => navigate("/register-business")}
            className="bg-navy hover:bg-navy/90 rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('dashboard:overview.newBusiness')}
          </Button>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.filter(stat => stat.visible !== false).map((stat, index) => (
          <Card 
            key={index} 
            className={`border-0 shadow-sm transition-shadow ${stat.clickable !== false ? 'hover:shadow-md cursor-pointer' : 'opacity-75'}`}
            onClick={stat.clickable !== false ? stat.onClick : undefined}
            data-testid={`stat-card-${index}`}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-grey text-sm">{stat.title}</p>
                  <p className="font-heading font-bold text-2xl text-navy mt-1">
                    {stat.value}
                  </p>
                </div>
                <div className={`w-12 h-12 ${stat.bg} rounded-xl flex items-center justify-center`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
              {stat.clickable !== false ? (
                <p className="text-xs text-grey mt-2 flex items-center gap-1">
                  <ChevronRight className="w-3 h-3" />
                  {t('dashboard:overview.clickForDetail')}
                </p>
              ) : (
                <p className="text-xs text-slate-400 mt-2">
                  -
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* My Businesses / Trainer's Businesses / Employee's Businesses */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center justify-between">
              <span>{(user?.role === "trainer" || user?.role === "employee") ? t('dashboard:overview.checkSettings') : t('dashboard:overview.checkSettings')}</span>
              {businesses.length > 0 && (
                <Badge variant="secondary">{businesses.filter(b => (user?.role !== "trainer" && user?.role !== "employee") || !b.is_own_business).length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Filter businesses based on role */}
            {(() => {
              const displayBusinesses = (user?.role === "trainer" || user?.role === "employee")
                ? businesses.filter(b => !b.is_own_business) // Çalıştığı işletmeler (kendi olmayan)
                : businesses;
              
              if (displayBusinesses.length > 0) {
                return (
                  <div className="space-y-3">
                    {displayBusinesses.slice(0, 3).map((business) => (
                      <div
                        key={business.business_id}
                        className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                        onClick={() => navigate(`/business/${business.business_id}`)}
                        data-testid={`business-item-${business.business_id}`}
                      >
                        <img
                          src={business.logo || business.images?.[0] || "https://images.pexels.com/photos/5412246/pexels-photo-5412246.jpeg"}
                          alt={business.name}
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-navy">{business.name}</h4>
                            {(user?.role === "trainer" || user?.role === "employee") ? (
                              <Badge 
                                variant="outline"
                                className="text-xs"
                              >
                                {business.is_manager ? t('employees:manager') : (user?.role === "trainer" ? t('common:trainer') : t('employees:employee'))}
                              </Badge>
                            ) : (
                              <Badge 
                                variant={business.is_branch ? "outline" : "default"}
                                className={business.is_branch ? "text-xs" : "text-xs bg-navy"}
                              >
                                {business.is_branch ? t('dashboard:locations.location') : t('dashboard:locations.mainBusiness')}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-grey">
                            <MapPin className="w-3 h-3" />
                            {business.city}
                            <Star className="w-3 h-3 ml-2 text-yellow-500" />
                            {business.rating?.toFixed(1) || "5.0"}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-grey" />
                      </div>
                    ))}
                  </div>
                );
              } else if (user?.role === "trainer" || user?.role === "employee") {
                return (
                  <div className="text-center py-6">
                    <Building2 className="w-10 h-10 text-grey mx-auto mb-2" />
                    <p className="text-grey text-sm">{t("common:henuzBirIsletmedeCalismiyorsunuz")}</p>
                    <p className="text-xs text-grey mt-1">
                      {user?.role === "trainer" 
                        ? "Bir işletme tarafından antrenör olarak eklendiğinizde burada görünecek"
                        : t('dashboard:overview.employeeNoBusiness')
                      }
                    </p>
                  </div>
                );
              } else {
                return (
                  <div className="text-center py-6">
                    <Building2 className="w-10 h-10 text-grey mx-auto mb-2" />
                    <p className="text-grey mb-3">{t('dashboard:overview.noBusinessYet')}</p>
                    <Button
                      onClick={() => navigate("/register-business")}
                      className="bg-navy hover:bg-navy/90 rounded-xl"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {t('dashboard:overview.addBusiness')}
                    </Button>
                  </div>
                );
              }
            })()}
          </CardContent>
        </Card>
        
        {/* Kendi İşletmelerim - Trainer ve Employee için */}
        {(user?.role === "trainer" || user?.role === "employee") && (
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center justify-between">
                <span>{t('dashboard:overview.myBusinesses')}</span>
                {businesses.filter(b => b.is_own_business).length > 0 && (
                  <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                    {businesses.filter(b => b.is_own_business).length}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const ownBusinesses = businesses.filter(b => b.is_own_business);
                
                if (ownBusinesses.length > 0) {
                  return (
                    <div className="space-y-3">
                      {ownBusinesses.slice(0, 3).map((business) => (
                        <div
                          key={business.business_id}
                          className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 hover:bg-emerald-100 transition-colors cursor-pointer"
                          onClick={() => navigate(`/business/${business.business_id}`)}
                          data-testid={`own-business-item-${business.business_id}`}
                        >
                          <img
                            src={business.logo || business.images?.[0] || "https://images.pexels.com/photos/5412246/pexels-photo-5412246.jpeg"}
                            alt={business.name}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-navy">{business.name}</h4>
                              <Badge className="text-xs bg-emerald-500">{t('dashboard:overview.myOwnBusiness')}</Badge>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-grey">
                              <MapPin className="w-3 h-3" />
                              {business.city}
                              <Star className="w-3 h-3 ml-2 text-yellow-500" />
                              {business.rating?.toFixed(1) || "5.0"}
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-grey" />
                        </div>
                      ))}
                    </div>
                  );
                } else {
                  return (
                    <div className="text-center py-6">
                      <Building2 className="w-10 h-10 text-emerald-300 mx-auto mb-2" />
                      <p className="text-grey mb-1">{t('dashboard:overview.noOwnBusiness')}</p>
                      <p className="text-xs text-grey mb-4">
                        {t('dashboard:overview.ownBusinessHint')}
                      </p>
                      <Button
                        onClick={() => navigate("/register-business")}
                        className="bg-emerald-600 hover:bg-emerald-700 rounded-xl"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {t('dashboard:overview.addBusiness')}
                      </Button>
                    </div>
                  );
                }
              })()}
            </CardContent>
          </Card>
        )}

        {/* Upcoming Reservations */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center justify-between">
              <span>{t('dashboard:overview.upcomingReservations')}</span>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{stats.today_reservations}</Badge>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate("/dashboard/reservations")}
                  className="text-xs"
                >
                  {t('dashboard:overview.viewAll')}
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.upcoming_reservations && stats.upcoming_reservations.length > 0 ? (
              <div className="space-y-3">
                {stats.upcoming_reservations.map((res, idx) => (
                  <div 
                    key={res.reservation_id || idx}
                    className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                    onClick={() => navigate("/dashboard/reservations")}
                  >
                    <div className="w-14 text-center bg-purple-100 rounded-lg p-2">
                      <p className="font-bold text-purple-600 text-sm">{res.start_time}</p>
                      <p className="text-xs text-grey">{res.end_time}</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-navy truncate">
                        {res.member_name || t('dashboard:overview.memberLabel')}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-grey">
                        <span className="flex items-center gap-1">
                          <Dumbbell className="w-3 h-3" />
                          {res.trainer_name || getLocalizedBranch(res.branch) || t('common:trainer')}
                        </span>
                        {res.room_name && (
                          <span className="flex items-center gap-1">
                            <DoorOpen className="w-3 h-3" />
                            {res.room_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <Badge 
                      variant="outline"
                      className={res.status === "confirmed" ? "bg-green-50 text-green-600 border-green-200" : "bg-orange-50 text-orange-600 border-orange-200"}
                    >
                      {res.status === "confirmed" ? t('dashboard:overview.confirmed') : t('dashboard:overview.pending')}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : stats.today_reservations > 0 ? (
              <div className="text-center py-4 text-grey">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Ders detayları yükleniyor...</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-grey mx-auto mb-3" />
                <p className="text-grey">{t('dashboard:overview.noLessonsToday')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue Details Modal */}
      {showRevenueModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            // Close modal when clicking on overlay (not on modal content)
            if (e.target === e.currentTarget) {
              setShowRevenueModal(false);
            }
          }}
        >
          <Card className="w-full max-w-xl max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-orange-500" />
                  {t('dashboard:overview.revenueDetail')}
                </span>
                <Button variant="ghost" size="icon" onClick={() => setShowRevenueModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingRevenue ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy mx-auto" />
                  <p className="text-grey mt-2">{t('common:loading')}</p>
                </div>
              ) : revenueDetails ? (
                <div className="space-y-4">
                  {/* Summary - Clickable cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div 
                      className="bg-green-50 rounded-xl p-3 text-center cursor-pointer hover:bg-green-100 transition-colors"
                      onClick={() => { setShowRevenueModal(false); navigate("/dashboard/payments"); }}
                    >
                      <p className="text-xs text-grey">{t('dashboard:overview.memberPayments')}</p>
                      <p className="font-bold text-green-600 text-sm">+{formatPrice((revenueDetails.income || 0))}</p>
                    </div>
                    <div 
                      className="bg-red-50 rounded-xl p-3 text-center cursor-pointer hover:bg-red-100 transition-colors"
                      onClick={() => { setShowRevenueModal(false); navigate("/dashboard/reports"); }}
                    >
                      <p className="text-xs text-grey">{t('dashboard:overview.refund')}</p>
                      <p className="font-bold text-red-600 text-sm">-{formatPrice((revenueDetails.refunds || 0))}</p>
                    </div>
                    <div 
                      className="bg-purple-50 rounded-xl p-3 text-center cursor-pointer hover:bg-purple-100 transition-colors"
                      onClick={() => { setShowRevenueModal(false); navigate("/dashboard/commissions"); }}
                    >
                      <p className="text-xs text-grey">{t('dashboard:overview.commissionPayment')}</p>
                      <p className="font-bold text-purple-600 text-sm">-{formatPrice((revenueDetails.commissions || 0))}</p>
                    </div>
                    <div 
                      className="bg-blue-50 rounded-xl p-3 text-center cursor-pointer hover:bg-blue-100 transition-colors"
                      onClick={() => { setShowRevenueModal(false); navigate("/dashboard/salary-payments"); }}
                    >
                      <p className="text-xs text-grey">{t('dashboard:overview.salaryExtra')}</p>
                      <p className="font-bold text-blue-600 text-sm">-{formatPrice(((revenueDetails.salaries || 0) + (revenueDetails.bonus || 0)))}</p>
                    </div>
                    <div 
                      className="bg-slate-100 rounded-xl p-3 text-center cursor-pointer hover:bg-slate-200 transition-colors"
                      onClick={() => { setShowRevenueModal(false); navigate("/dashboard/business-expenses"); }}
                    >
                      <p className="text-xs text-grey">{t('dashboard:overview.businessExpense')}</p>
                      <p className="font-bold text-slate-700 text-sm">-{formatPrice((revenueDetails.business_expenses || 0))}</p>
                    </div>
                    <div 
                      className="bg-amber-50 rounded-xl p-3 text-center cursor-pointer hover:bg-amber-100 transition-colors"
                      onClick={() => { setShowRevenueModal(false); navigate("/dashboard/trainer-collections"); }}
                    >
                      <p className="text-xs text-grey">{t('dashboard:overview.incomingRentCommission')}</p>
                      <p className="font-bold text-amber-600 text-sm">+{formatPrice((revenueDetails.trainer_collections || 0))}</p>
                    </div>
                    <div 
                      className="bg-orange-50 rounded-xl p-3 text-center cursor-pointer hover:bg-orange-100 transition-colors col-span-2 sm:col-span-2"
                      onClick={() => { setShowRevenueModal(false); navigate("/dashboard/reports"); }}
                    >
                      <p className="text-xs text-grey">{t('common:netRevenue')}</p>
                      <p className={`font-bold text-sm ${(revenueDetails.net_revenue || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {(revenueDetails.net_revenue || 0) >= 0 ? '+' : '-'}{formatPrice(Math.abs(revenueDetails.net_revenue || 0))}
                      </p>
                    </div>
                  </div>

                  {/* Formula */}
                  <div className="text-center text-xs text-grey bg-slate-50 rounded-lg p-2">
                    {t('dashboard:overview.revenueFormula')}
                  </div>

                  {/* Cash Flow - All transactions */}
                  {revenueDetails.recent_payments && revenueDetails.recent_payments.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-navy mb-2">{t('dashboard:overview.paymentFlow')}</p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {revenueDetails.recent_payments.slice(0, 10).map((payment, idx) => (
                          <div key={payment.payment_id || idx} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded-lg">
                            <div className="flex-1">
                              <p className="font-medium">{payment.member_name || t('dashboard:overview.transaction')}</p>
                              <p className="text-xs text-grey flex items-center gap-1">
                                {payment.payment_date?.split("T")[0]}
                                {payment.type === "refund" && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200">{t('dashboard:overview.refund')}</Badge>}
                                {payment.type === "bonus" && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-600 border-amber-200">{t('dashboard:overview.extraPayment')}</Badge>}
                                {payment.type === "salary" && <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-600 border-blue-200">{t('trainers:salary')}</Badge>}
                                {payment.type === "commission" && <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-600 border-purple-200">{t('dashboard:overview.commissionPayment')}</Badge>}
                                {payment.type === "trainer_rent" && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-600 border-amber-200">{t('dashboard:overview.rentCollection')}</Badge>}
                                {payment.type === "trainer_commission" && <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-600 border-purple-200">{t('dashboard:overview.commissionPayment')}</Badge>}
                                {payment.type === "employee_commission" && <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-600 border-purple-200">{t('dashboard:overview.commissionPayment')}</Badge>}
                                {payment.type === "received_commission" && <Badge variant="outline" className="text-[10px] bg-green-50 text-green-600 border-green-200">{t('dashboard:overview.incomingCommission')}</Badge>}
                                {payment.type === "business_expense" && <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-700 border-slate-300">{t('dashboard:overview.businessExpense')}</Badge>}
                                {payment.type === "package_sale" && <Badge variant="outline" className="text-[10px] bg-green-50 text-green-600 border-green-200">{t('dashboard:overview.packagePayment')}</Badge>}
                                {!payment.type && payment.member_package_id && <Badge variant="outline" className="text-[10px] bg-green-50 text-green-600 border-green-200">{t('dashboard:overview.packagePayment')}</Badge>}
                              </p>
                            </div>
                            <span className={`font-semibold ${payment.amount >= 0 ? "text-green-600" : "text-red-600"}`}>
                              {payment.amount >= 0 ? "+" : "-"}{formatPrice(Math.abs(payment.amount))}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quick Action Button - only if user can access reports */}
                  {(user?.role === "business" || (userPermissions?.is_manager && canAccessPage("/dashboard/reports"))) && (
                    <Button 
                      className="w-full bg-navy hover:bg-navy/90 rounded-xl"
                      onClick={() => {
                        setShowRevenueModal(false);
                        navigate("/dashboard/reports");
                      }}
                    >
                      <BarChart className="w-4 h-4 mr-2" />
                      {t('dashboard:overview.detailedReports')}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CreditCard className="w-12 h-12 text-grey mx-auto mb-3" />
                  <p className="text-grey">{t('dashboard:overview.noPaymentThisMonth')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Member Progress (for members) */}
      {user?.role === "member" && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">{t('dashboard:overview.myPackageStatus')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-grey">{t('packages:remainingSessions')}</span>
                <span className="font-bold text-navy">8 / 12</span>
              </div>
              <Progress value={66} className="h-2" />
              <div className="flex items-center gap-2 text-sm text-grey">
                <Clock className="w-4 h-4" />
                <span>{t('dashboard:overview.packageEnd')}: 15 Ocak 2025</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ 
  user, 
  isOpen, 
  onClose, 
  onLogout, 
  subscription, 
  trainerBusinesses, 
  selectedTrainerBusiness, 
  onTrainerBusinessChange,
  businesses,
  selectedBusiness,
  onBusinessChange,
  userPermissions,  // User's permissions for current business
  employeeBusinesses,  // Employee's businesses
  activeBusinessId,
  unreadMessageCount = 0,  // Unread message count for badge
  pendingReservationsCount = 0,  // Pending reservations count for badge
  pendingBusinessCount = 0,  // Admin: Pending business approvals count
  pendingAddressCount = 0,  // Admin: Pending address change requests count
  pendingVisibilityCount = 0,  // Admin: Pending trainer visibility requests count
  memberBadge = null,  // { total, package_requests, link_pending }
  groupClassesEnabled = false,  // Üye: rezervasyon linki açık işletmesi var mı
  pendingDemoCount = 0
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(['dashboard', 'common', "reports", "packages", "employees", "admin"]);
  const [expandedGroups, setExpandedGroups] = useState(["genel"]);

  // Plan hierarchy for comparison
  // Plan hierarchy for feature access control
  // pro = professional level access (all features)
  // free = basic/no features
  const planHierarchy = { 
    none: 0, 
    free: 0, 
    basic: 1, 
    starter: 1,
    pro: 2,  // Pro plan - full access
    professional: 2, 
    premium: 3
  };
  const currentPlanLevel = planHierarchy[subscription?.plan] || 0;

  // Map menu paths to module IDs for dynamic access control
  const pathToModuleMap = {
    "/dashboard/members": "members",
    "/dashboard/member-forms": "member_forms",
    "/dashboard/qr-scan": "qr_scan",
    "/dashboard/qr-checkin": "qr_scan",
    "/dashboard/measurements": "measurements",
    "/dashboard/reservations": "reservations",
    "/dashboard/payments": "payments",
    "/dashboard/salary-payments": "salary_payments",
    "/dashboard/bonus-payments": "bonus_payments",
    "/dashboard/business-expenses": "business_expenses",
    "/dashboard/trainer-collections": "trainer_collections",
    "/dashboard/commissions": "commissions",
    "/dashboard/reports": "reports",
    "/dashboard/rooms": "rooms",
    "/dashboard/packages": "packages",
    "/dashboard/all-packages": "all_packages",
    "/dashboard/price-list": "price_list",
    "/dashboard/trainers": "trainers",
    "/dashboard/employees": "employees",
    "/dashboard/branches": "branches",
    "/dashboard/analytics": "analytics",
    "/dashboard/surveys": "surveys",
    "/dashboard/events": "events",
    "/dashboard/evaluations": "evaluations",
    "/dashboard/goals": "goals",
    "/dashboard/messages": "messages",
    "/dashboard/ratings": "ratings",
    "/dashboard/settings": "settings",
    "/dashboard/subscription": "subscription",
  };

  // Check if user has access to a menu item based on subscription
  const hasAccess = (requiredPlan, path) => {
    // If no plan required and no path, allow access
    if (!requiredPlan && !path) return true;
    
    // Check subscription-based module access
    const moduleId = pathToModuleMap[path];
    
    if (subscription) {
      // Check allowed_modules from dynamic subscription
      const moduleList = subscription.allowed_modules || subscription.features || [];
      
      // 'all' marker means full access
      if (Array.isArray(moduleList) && moduleList.includes('all')) {
        return true;
      }
      
      // Check specific module
      if (moduleId && Array.isArray(moduleList) && moduleList.length > 0) {
        return moduleList.includes(moduleId);
      }
    }
    
    // Fallback to legacy plan-based check
    if (!requiredPlan) return true;
    const requiredLevel = planHierarchy[requiredPlan] || 0;
    return currentPlanLevel >= requiredLevel;
  };

  const toggleGroup = (groupId) => {
    // Accordion behavior - only one group open at a time
    setExpandedGroups(prev => 
      prev.includes(groupId) 
        ? []  // Close if already open
        : [groupId]  // Open only this one, close others
    );
  };

  // Grouped menu structure for business owners
  const businessMenuGroups = [
    {
      id: "genel",
      label: t('dashboard:groups.general'),
      icon: LayoutDashboard,
      items: [
        { icon: Map, label: t('dashboard:sidebar.map'), path: "/" },
        { icon: LayoutDashboard, label: t('dashboard:sidebar.overview'), path: "/dashboard" },
        { icon: Building2, label: t('dashboard:sidebar.branches'), path: "/dashboard/branches", requiresPlan: "pro" },
        { icon: TrendingUp, label: t('dashboard:sidebar.analytics'), path: "/dashboard/analytics", requiresPlan: "pro" },
        { icon: Crown, label: t('dashboard:sidebar.subscription'), path: "/dashboard/subscription" },
      ]
    },
    {
      id: "uyeler",
      label: t('dashboard:groups.memberManagement'),
      icon: Users,
      items: [
        { icon: Users, label: t('dashboard:sidebar.members'), path: "/dashboard/members", requiresPlan: "pro" },
        { icon: Package, label: t('dashboard:sidebar.allPackages'), path: "/dashboard/all-packages", requiresPlan: "pro" },
        { icon: FileText, label: t('dashboard:sidebar.memberForms'), path: "/dashboard/member-forms", requiresPlan: "pro" },
        { icon: QrCode, label: t('dashboard:sidebar.qrScan'), path: "/dashboard/qr-scan", requiresPlan: "pro" },
        { icon: Activity, label: t('dashboard:sidebar.measurements'), path: "/dashboard/measurements", requiresPlan: "pro" },
      ]
    },
    {
      id: "rezervasyon",
      label: null,
      items: [
        { icon: Calendar, label: t('dashboard:sidebar.reservations'), path: "/dashboard/reservations", requiresPlan: "pro" },
      ]
    },
    {
      id: "odemeler",
      label: t('dashboard:groups.payments'),
      icon: Wallet,
      items: [
        { icon: CreditCard, label: t('dashboard:sidebar.packagePayments'), path: "/dashboard/payments", requiresPlan: "pro" },
        { icon: Banknote, label: t('dashboard:sidebar.salaryPayments'), path: "/dashboard/salary-payments", requiresPlan: "pro" },
        { icon: Gift, label: t('dashboard:sidebar.bonusPayments'), path: "/dashboard/bonus-payments", requiresPlan: "pro" },
        { icon: Receipt, label: t('dashboard:sidebar.businessExpenses'), path: "/dashboard/business-expenses", requiresPlan: "pro" },
        { icon: Building2, label: t('dashboard:sidebar.trainerCollections'), path: "/dashboard/trainer-collections", requiresPlan: "pro" },
        { icon: TrendingUp, label: t('dashboard:sidebar.commissions'), path: "/dashboard/commissions", requiresPlan: "pro" },
        { icon: FileText, label: t('dashboard:sidebar.reports'), path: "/dashboard/reports", requiresPlan: "pro" },
      ]
    },
    {
      id: "isletme",
      label: t('dashboard:groups.businessSettings'),
      icon: Layers,
      items: [
        { icon: DoorOpen, label: t('dashboard:sidebar.rooms'), path: "/dashboard/rooms", requiresPlan: "pro" },
        { icon: Package, label: t('dashboard:sidebar.packages'), path: "/dashboard/packages", requiresPlan: "pro" },
        { icon: Tag, label: t('dashboard:sidebar.priceLists'), path: "/dashboard/price-list", requiresPlan: "pro" },
        { icon: Dumbbell, label: t('dashboard:sidebar.trainers'), path: "/dashboard/trainers", requiresPlan: "pro" },
        { icon: Briefcase, label: t('dashboard:sidebar.employees'), path: "/dashboard/employees", requiresPlan: "pro" },
        ...( (user?.role === "business" || selectedBusiness?.is_own_business || userPermissions?.is_owner || selectedBusiness?.owner_id === user?.user_id) ? [{ icon: History, label: t('dashboard:sidebar.auditLog', 'Denetim Kaydı'), path: "/dashboard/audit-log" }] : []),
      ]
    },
    {
      id: "iletisim",
      label: t('dashboard:groups.communication'),
      icon: MessageCircle,
      items: [
        { icon: MessageCircle, label: t('dashboard:sidebar.messages'), path: "/dashboard/messages" },
        { icon: Star, label: t('dashboard:sidebar.evaluations'), path: "/dashboard/ratings" },
        { icon: ClipboardList, label: t('dashboard:sidebar.surveys'), path: "/dashboard/surveys" },
        { icon: Calendar, label: t('dashboard:sidebar.events'), path: "/dashboard/events" },
        { icon: MessageSquare, label: t('dashboard:sidebar.memberEvaluations'), path: "/dashboard/evaluations" },
        { icon: Target, label: t('dashboard:sidebar.goals'), path: "/dashboard/goals" },
      ]
    },
    {
      id: "admin",
      label: t('dashboard:groups.admin'),
      icon: ShieldCheck,
      adminOnly: true,
      items: [
        { icon: ShieldCheck, label: t('dashboard:sidebar.businessApprovals'), path: "/dashboard/business-approvals", badgeCount: pendingBusinessCount },
        { icon: MapPin, label: t('dashboard:sidebar.addressRequests'), path: "/dashboard/address-requests", badgeCount: pendingAddressCount },
        { icon: Eye, label: t('dashboard:sidebar.trainerListRequests'), path: "/dashboard/trainer-visibility", badgeCount: pendingVisibilityCount },
        { icon: Key, label: t('dashboard:sidebar.generateCode'), path: "/dashboard/admin-codes" },
        { icon: Settings, label: t('dashboard:sidebar.adminSettings'), path: "/dashboard/admin-settings" },
        { icon: Languages, label: t('dashboard:sidebar.translationManagement'), path: "/dashboard/translations" },
        { icon: Trash2, label: t('dashboard:sidebar.resetDb'), path: "/dashboard/reset-db" },
      ]
    },
    {
      id: "ayarlar",
      label: null,
      items: [
        { icon: Settings, label: t('dashboard:sidebar.settings'), path: "/dashboard/settings" },
      ]
    }
  ];

  // Member menu groups - ÜYE PANELİ
  const memberMenuGroups = [
    {
      id: "genel",
      label: t('dashboard:groups.general'),
      icon: LayoutDashboard,
      items: [
        { icon: Map, label: t('dashboard:sidebar.map'), path: "/" },
        { icon: LayoutDashboard, label: t('dashboard:sidebar.overview'), path: "/dashboard" },
      ]
    },
    {
      id: "rezervasyon",
      label: t('dashboard:sidebar.reservations'),
      icon: Calendar,
      items: [
        { icon: Calendar, label: t('dashboard:groups.myReservations'), path: "/dashboard/my-reservations" },
        ...(groupClassesEnabled ? [{ icon: Users, label: "Online Ders Programı", path: "/dashboard/group-classes" }] : []),
        { icon: Clock, label: t('dashboard:groups.createRequest'), path: "/dashboard/create-request" },
        { icon: ClipboardList, label: t('dashboard:groups.myRequests'), path: "/dashboard/my-requests" },
        { icon: QrCode, label: t('dashboard:sidebar.qrCheckin'), path: "/dashboard/qr-checkin" },
      ]
    },
    {
      id: "paketler",
      label: t('dashboard:groups.myPackages'),
      icon: Package,
      items: [
        { icon: Package, label: t('dashboard:groups.activePackages'), path: "/dashboard/my-packages" },
        { icon: CreditCard, label: t('dashboard:groups.paymentHistory'), path: "/dashboard/my-payments" },
      ]
    },
    {
      id: "profil",
      label: t('dashboard:groups.profile'),
      icon: User,
      items: [
        { icon: Activity, label: t('dashboard:groups.myMeasurements'), path: "/dashboard/my-measurements" },
        { icon: ClipboardList, label: t('dashboard:groups.mySurveys'), path: "/dashboard/my-surveys" },
        { icon: Calendar, label: t('dashboard:groups.myEvents'), path: "/dashboard/my-events" },
        { icon: MessageSquare, label: t('dashboard:groups.myEvaluations'), path: "/dashboard/my-evaluations" },
        { icon: MessageCircle, label: t('dashboard:groups.myMessages'), path: "/dashboard/messages" },
        { icon: Settings, label: t('dashboard:groups.mySettings'), path: "/dashboard/settings" },
      ]
    }
  ];

  // Permission-based menu filtering for trainers and employees
  // Maps menu paths to permission keys (must match keys in TrainersModule.jsx and EmployeesModule.jsx)
  const menuPermissionMap = {
    // Genel
    "/dashboard": "dashboard_overview",
    "/dashboard/overview": "dashboard_overview",
    "/dashboard/branches": "branches",
    "/dashboard/analytics": "analytics",
    "/dashboard/subscription": "subscription",
    "/dashboard/settings": "settings",
    // Üye Yönetimi
    "/dashboard/members": "members_list",
    "/dashboard/all-packages": "all_packages",
    "/dashboard/member-forms": "member_forms",
    "/dashboard/qr-scan": "qr_scan",
    "/dashboard/measurements": "measurements",
    // Rezervasyonlar
    "/dashboard/reservations": "reservations",
    // Ödemeler
    "/dashboard/payments": "payments",
    "/dashboard/salary-payments": "salary_payments",
    "/dashboard/bonus-payments": "bonus_payments",
    "/dashboard/business-expenses": "business_expenses",
    "/dashboard/trainer-collections": "trainer_collections",
    "/dashboard/commissions": "commissions",
    "/dashboard/reports": "reports",
    // İşletme Ayarları
    "/dashboard/rooms": "rooms",
    "/dashboard/packages": "packages",
    "/dashboard/price-list": "price_list",
    "/dashboard/trainers": "trainers",
    "/dashboard/employees": "employees",
    "/dashboard/settings": "settings",
    // İletişim
    "/dashboard/messages": "messages",
    "/dashboard/ratings": "ratings",
    "/dashboard/surveys": "surveys",
    "/dashboard/events": "events",
    "/dashboard/evaluations": "evaluations",
    "/dashboard/goals": "goals",
  };
  
  // Yönetici-only (manager required) paths - bu sayfalar yönetici olmayan için her zaman kilitli
  const managerOnlyPaths = [
    "/dashboard/branches",
    "/dashboard/analytics",
    "/dashboard/subscription",
    "/dashboard/salary-payments",
    "/dashboard/bonus-payments",
    "/dashboard/business-expenses",
    "/dashboard/trainer-collections",
    "/dashboard/commissions",
    "/dashboard/reports",
    "/dashboard/rooms",
    "/dashboard/packages",
    "/dashboard/price-list",
    "/dashboard/trainers",
    "/dashboard/employees",
    "/dashboard/settings",
  ];
  
  // Check if user has permission for a menu item
  // Helper function to check permissions - takes currentUserPermissions as parameter
  const hasPermission = (path, currentUserPermissions) => {
    // Business owners always have all permissions
    if (user?.role === "business") return true;
    
    // Şirket ortağı ise tüm menülere ve sayfalara tam yetkili erişir
    if (currentUserPermissions?.is_co_owner === true || currentUserPermissions?.is_owner === true) return true;

    // ABONELİK ÖZEL KORUMASI:
    // İşletme sahibi veya ortağı değilse antrenör/çalışan asla aboneliğe erişemez!
    if (path === "/dashboard/subscription") {
      return false;
    }

    // AYARLAR ÖZEL KORUMASI:
    // Eğer ayarlar sayfasına giriliyorsa en az bir ayar izni veya ana 'settings' izni açık olmalı
    if (path === "/dashboard/settings") {
      const perms = currentUserPermissions?.permissions || {};
      const hasAnySettingPerm = perms.settings === true || 
                                perms.settings_business_info === true || 
                                perms.settings_pricing === true ||
                                perms.settings_working_hours === true;
      return Boolean(currentUserPermissions?.is_manager && hasAnySettingPerm);
    }

    // If user is owner of selected business (trainer with own business)
    if (user?.role === "trainer" && selectedTrainerBusiness?.is_own_business) return true;
    if (user?.role === "employee" && employeeBusinesses?.some(b => b.is_own_business && b.business_id === activeBusinessId)) return true;
    
    const isManager = currentUserPermissions?.is_manager || currentUserPermissions?.is_owner;
    
    // Check if this is a manager-only path
    const isManagerOnlyPath = managerOnlyPaths.includes(path);
    
    // Get the permissions object
    const perms = currentUserPermissions?.permissions;
    
    // Check specific permission from user's saved permissions
    const permKey = menuPermissionMap[path];
    
    // Debug log
    console.log(`[hasPermission] path: ${path}, permKey: ${permKey}, perms[permKey]: ${perms?.[permKey]}, isManager: ${isManager}, isManagerOnlyPath: ${isManagerOnlyPath}`);
    
    if (isManagerOnlyPath) {
      // Manager-only paths require being a manager
      if (!isManager) return false;
      // If manager, check if they have the specific permission (if perms loaded)
      if (permKey && perms) {
        return perms[permKey] === true;
      }
      // If permissions not loaded yet, assume manager has access to manager-only paths
      return isManager;
    }
    
    // For non-manager-only paths, check the permission
    if (!permKey) return true; // No permission required for unmapped paths
    
    // If permissions not loaded yet, allow access (will update when loaded)
    if (!perms) return true;
    
    // Check the specific permission
    const hasAccess = perms[permKey] === true;
    return hasAccess;
  };
  
  // Mark menu items as permission-locked (NOT filter/hide them)
  // This allows menus to be visible but locked with appropriate UI
  // Note: currentUserPermissions passed explicitly to avoid closure issues
  const filterMenuByPermissions = (groups, currentUserPermissions) => {
    const isManager = currentUserPermissions?.is_manager || currentUserPermissions?.is_owner;
    
    return groups.map(group => {
      return {
        ...group,
        items: group.items.map(item => {
          // Admin-only items - completely hide for non-admins
          if (item.adminOnly && user?.role !== "admin") return null;
          
          // Check if user has permission for this item
          const hasItemPermission = hasPermission(item.path, currentUserPermissions);
          
          // If no permission, mark as permissionLocked but keep visible
          return {
            ...item,
            permissionLocked: !hasItemPermission
          };
        }).filter(Boolean) // Remove null items (admin-only for non-admins)
      };
    }).filter(group => group.items.length > 0 || !group.label);
  };

  // Unified menu for trainers and employees (same as business, filtered by permissions)
  // Note: userPermissions is passed explicitly to avoid closure issues
  const getUnifiedUserMenu = (currentUserPermissions) => {
    const isOwnBusiness = Boolean(
      selectedTrainerBusiness?.is_own_business || 
      user?.has_own_business || 
      selectedBusiness?.is_own_business ||
      (trainerBusinesses && trainerBusinesses.some(b => b.is_own_business))
    );
    const isManager = currentUserPermissions?.is_manager || currentUserPermissions?.is_owner || currentUserPermissions?.is_co_owner;
    // Check if subscription allows full access (pro or higher)
    const fullAccessPlans = ['pro', 'premium', 'starter', 'basic'];
    const hasFullAccess = subscription?.is_active && fullAccessPlans.includes(subscription?.plan);
    const hasActiveSubscription = hasFullAccess;
    
    console.log("getUnifiedUserMenu called:", {
      role: user?.role,
      isOwnBusiness,
      isManager,
      subscriptionPlan: subscription?.plan,
      hasFullAccess,
      hasPermissions: !!currentUserPermissions,
      permissionsKeys: currentUserPermissions ? Object.keys(currentUserPermissions.permissions || {}) : []
    });
    
    // Base business menu (same for everyone)
    const baseMenu = [
      {
        id: "genel",
        label: t('dashboard:groups.general'),
        icon: LayoutDashboard,
        items: [
          { icon: Map, label: t('dashboard:sidebar.map'), path: "/" },
          { icon: LayoutDashboard, label: t('dashboard:sidebar.overview'), path: "/dashboard" },
          { icon: Building2, label: t('dashboard:sidebar.branches'), path: "/dashboard/branches", locked: !hasActiveSubscription, managerOnly: true },
          { icon: TrendingUp, label: t('dashboard:sidebar.analytics'), path: "/dashboard/analytics", locked: !hasActiveSubscription, managerOnly: true },
          { icon: Crown, label: t('dashboard:sidebar.subscription'), path: "/dashboard/subscription", managerOnly: true },
        ]
      },
      {
        id: "uyeler",
        label: t('dashboard:groups.memberManagement'),
        icon: Users,
        items: [
          { icon: Users, label: t('dashboard:sidebar.members'), path: "/dashboard/members", locked: !hasActiveSubscription },
          { icon: Package, label: t('dashboard:sidebar.allPackages'), path: "/dashboard/all-packages", locked: !hasActiveSubscription },
          { icon: FileText, label: t('dashboard:sidebar.memberForms'), path: "/dashboard/member-forms", locked: !hasActiveSubscription },
          { icon: QrCode, label: t('dashboard:sidebar.qrScan'), path: "/dashboard/qr-scan", locked: !hasActiveSubscription },
          { icon: Activity, label: t('dashboard:sidebar.measurements'), path: "/dashboard/measurements", locked: !hasActiveSubscription },
        ]
      },
      {
        id: "rezervasyon",
        label: null,
        items: [
          { icon: Calendar, label: t('dashboard:sidebar.reservations'), path: "/dashboard/reservations", locked: !hasActiveSubscription },
        ]
      },
      {
        id: "odemeler",
        label: t('dashboard:groups.payments'),
        icon: Wallet,
        items: [
          { icon: CreditCard, label: t('dashboard:sidebar.packagePayments'), path: "/dashboard/payments", locked: !hasActiveSubscription },
          { icon: Banknote, label: t('dashboard:sidebar.salaryPayments'), path: "/dashboard/salary-payments", locked: !hasActiveSubscription, managerOnly: true },
          { icon: Gift, label: t('dashboard:sidebar.bonusPayments'), path: "/dashboard/bonus-payments", locked: !hasActiveSubscription, managerOnly: true },
          { icon: Receipt, label: t('dashboard:sidebar.businessExpenses'), path: "/dashboard/business-expenses", locked: !hasActiveSubscription, managerOnly: true },
          { icon: Building2, label: t('dashboard:sidebar.trainerCollections'), path: "/dashboard/trainer-collections", locked: !hasActiveSubscription, managerOnly: true },
          { icon: TrendingUp, label: t('dashboard:sidebar.commissions'), path: "/dashboard/commissions", locked: !hasActiveSubscription, managerOnly: true },
          { icon: FileText, label: t('dashboard:sidebar.reports'), path: "/dashboard/reports", locked: !hasActiveSubscription, managerOnly: true },
        ]
      },
      {
        id: "isletme",
        label: t('dashboard:groups.businessSettings'),
        icon: Layers,
        managerOnly: true,
        items: [
          { icon: DoorOpen, label: t('dashboard:sidebar.rooms'), path: "/dashboard/rooms", locked: !hasActiveSubscription, managerOnly: true },
          { icon: Package, label: t('dashboard:sidebar.packages'), path: "/dashboard/packages", locked: !hasActiveSubscription, managerOnly: true },
          { icon: Tag, label: t('dashboard:sidebar.priceLists'), path: "/dashboard/price-list", locked: !hasActiveSubscription, managerOnly: true },
          { icon: Dumbbell, label: t('dashboard:sidebar.trainers'), path: "/dashboard/trainers", locked: !hasActiveSubscription, managerOnly: true },
          { icon: Briefcase, label: t('dashboard:sidebar.employees'), path: "/dashboard/employees", locked: !hasActiveSubscription, managerOnly: true },
          ...( (user?.role === "business" || isOwnBusiness || user?.has_own_business)
            ? [{ icon: History, label: t('dashboard:sidebar.auditLog', 'Denetim Kaydı'), path: "/dashboard/audit-log" }]
            : []
          ),
        ]
      },
    ];
    
    // Personal menu for trainers (their own stats)
    if (user?.role === "trainer" && !isOwnBusiness) {
      baseMenu.push({
        id: "kisisel",
        label: t('dashboard:groups.profile'),
        icon: User,
        items: [
          { icon: Calendar, label: t('memberDashboard:sidebar.classes'), path: "/dashboard/my-classes", locked: !hasActiveSubscription },
          { icon: TrendingUp, label: t('dashboard:sidebar.commissions'), path: "/dashboard/my-commissions", locked: !hasActiveSubscription },
          { icon: Wallet, label: t('memberDashboard:sidebar.finances'), path: "/dashboard/my-finances", locked: !hasActiveSubscription },
        ]
      });
    }
    
    // Personal menu for employees (their own stats)
    if (user?.role === "employee" && !isOwnBusiness) {
      baseMenu.push({
        id: "kisisel",
        label: t('dashboard:groups.profile'),
        icon: User,
        items: [
          { icon: TrendingUp, label: t('dashboard:sidebar.commissions'), path: "/dashboard/my-commissions", locked: !hasActiveSubscription },
          { icon: Wallet, label: t('memberDashboard:sidebar.finances'), path: "/dashboard/my-finances", locked: !hasActiveSubscription },
        ]
      });
    }
    
    // Communication
    baseMenu.push({
      id: "iletisim",
      label: t('dashboard:groups.communication'),
      icon: MessageCircle,
      items: [
        { icon: MessageCircle, label: t('dashboard:sidebar.messages'), path: "/dashboard/messages" },
        { icon: Star, label: t('dashboard:sidebar.evaluations'), path: "/dashboard/ratings" },
        { icon: ClipboardList, label: t('dashboard:sidebar.surveys'), path: "/dashboard/surveys" },
        { icon: CalendarDays, label: t('dashboard:sidebar.events'), path: "/dashboard/events" },
        { icon: ThumbsUp, label: t('dashboard:sidebar.memberEvaluations'), path: "/dashboard/evaluations" },
        { icon: Target, label: t('dashboard:sidebar.goals'), path: "/dashboard/goals" },
      ]
    });
    
    // Admin menu (only for admins)
    if (user?.is_admin === true) {
      baseMenu.push({
        id: "admin",
        label: t('dashboard:groups.admin'),
        icon: ShieldCheck,
        adminOnly: true,
        items: [
          { icon: ShieldCheck, label: t('dashboard:sidebar.businessApprovals'), path: "/dashboard/business-approvals", badgeCount: pendingBusinessCount },
          { icon: MapPin, label: t('dashboard:sidebar.addressRequests'), path: "/dashboard/address-requests", badgeCount: pendingAddressCount },
          { icon: Eye, label: t('dashboard:sidebar.trainerListRequests'), path: "/dashboard/trainer-visibility", badgeCount: pendingVisibilityCount },
          { icon: Key, label: t('dashboard:sidebar.generateCode'), path: "/dashboard/admin-codes" },
          { icon: Settings, label: t('dashboard:sidebar.adminSettings'), path: "/dashboard/admin-settings" },
          { icon: Languages, label: t('dashboard:sidebar.translationManagement'), path: "/dashboard/translations" },
          { icon: Trash2, label: t('dashboard:sidebar.resetDb'), path: "/dashboard/reset-db" },
        ]
      });
    }
    
    // Ayarlar menüsünü eklerken doğrudan izin kontrolü ile kilit durumunu belirleyin:
    const hasAnySettingsPermission = Boolean(
      isManager && (
        currentUserPermissions?.permissions?.settings === true ||
        currentUserPermissions?.permissions?.settings_business_info === true ||
        currentUserPermissions?.permissions?.settings_pricing === true ||
        currentUserPermissions?.permissions?.settings_working_hours === true ||
        currentUserPermissions?.permissions?.settings_branches === true
      )
    );

    const isSettingsLocked = !isOwnBusiness && !hasAnySettingsPermission;

    baseMenu.push({
      id: "ayarlar",
      label: null,
      managerOnly: true,
      items: [
        { 
          icon: Settings, 
          label: t('dashboard:sidebar.settings'), 
          path: "/dashboard/settings", 
          managerOnly: true,
          locked: isSettingsLocked,
          permissionLocked: isSettingsLocked
        },
      ]
    });
    
    // Filter by permissions (for trainers and employees who are not owners)
    if (!isOwnBusiness && user?.role !== "business") {
      return filterMenuByPermissions(baseMenu, currentUserPermissions);
    }
    
    return baseMenu;
  };
  
  // Legacy trainer menu - redirect to unified menu
  // Note: Pass userPermissions explicitly to ensure fresh value
  const trainerMenuGroups = user?.role === "trainer" ? getUnifiedUserMenu(userPermissions) : [];
  
  // Employee menu - same as trainer, using unified menu with permissions
  const employeeMenuGroups = user?.role === "employee" ? getUnifiedUserMenu(userPermissions) : [];

  // Select menu based on user role
  const menuGroups = user?.role === "member" 
    ? memberMenuGroups 
    : user?.role === "trainer" 
      ? trainerMenuGroups 
      : user?.role === "employee"
        ? employeeMenuGroups
        : businessMenuGroups;

  // Filter admin-only groups
  const filteredGroups = menuGroups.filter(group => 
    !group.adminOnly || user?.is_admin === true
  );

  // Filter menu items by subscription allowed_modules
  // IMPORTANT: Don't hide menus, just mark them as locked
  const filterBySubscriptionModules = (groups) => {
    // Always show all menus - they will be marked as locked if not in allowed_modules
    return groups;
  };

  // Apply subscription filter
  const subscriptionFilteredGroups = filterBySubscriptionModules(filteredGroups);

  const renderMenuItem = (item, onClose, isTopLevel = false) => {
    const isActive = location.pathname === item.path;
    const isLockedByPlan = item.requiresPlan && !hasAccess(item.requiresPlan, item.path);
    const isLockedBySubscription = item.locked === true;

    // AYARLAR MENÜSÜ KİLİT KONTROLÜ:
    const isSettingsPath = item.path === "/dashboard/settings";
    const hasAnySettingsPermission = Boolean(
      (userPermissions?.is_manager || userPermissions?.is_owner || userPermissions?.is_co_owner) && (
        userPermissions?.permissions?.settings === true ||
        userPermissions?.permissions?.settings_business_info === true ||
        userPermissions?.permissions?.settings_pricing === true ||
        userPermissions?.permissions?.settings_working_hours === true ||
        userPermissions?.permissions?.settings_branches === true
      )
    );
    // Kendi işletmesi değilse ve ayar izni yoksa kilitle:
    const isSettingsLocked = isSettingsPath && !selectedTrainerBusiness?.is_own_business && user?.role !== "business" && !userPermissions?.is_co_owner && !hasAnySettingsPermission;

    const isLockedByPermission = item.permissionLocked === true || isSettingsLocked;
    
    const moduleId = pathToModuleMap[item.path];
    const allowedModules = subscription?.allowed_modules || subscription?.features || [];
    
    const hasAllowedModules = Array.isArray(allowedModules) && allowedModules.length > 0;
    const isLockedByAllowedModules = moduleId && hasAllowedModules && !allowedModules.includes('all') && !allowedModules.includes(moduleId);
    
    // Ayarlar sayfası ASLA isNeverLocked olamaz:
    const neverLockPaths = ['/dashboard/messages', '/dashboard/overview', '/'];
    const isNeverLocked = !isSettingsPath && (neverLockPaths.some(p => item.path === p) || item.path === '/dashboard');
    
    // Final lock: Ayarlar kilitliyse veya diğer kilitlerden biri varsa kilit ikonunu yak:
    const isLocked = isSettingsLocked || item.locked === true || (!isNeverLocked && (isLockedByPlan || isLockedBySubscription || isLockedByAllowedModules || isLockedByPermission));
    
    const handleClick = (e) => {
      if (isLocked) {
        e.preventDefault();
        // Different messages for different lock reasons
        if (isLockedByPermission) {
          toast.error(t("common:toast_buMenuyeErisimIzninizYok"));
        } else {
          toast.error(t("admin:upgradeRequired"), {
            action: {
              label: t("admin:upgradePlan"),
              onClick: () => navigate("/dashboard/subscription")
            }
          });
        }
        return;
      } 

      // HARİTA TIKLAMASI KONTROLÜ:
      // Eğer menüdeki "Harita" butonuna tıklandıysa, doğrudan ?map=1 ile yönlendir
      if (item.path === "/") {
        e.preventDefault();
        onClose();
        navigate("/?map=1");
        return;
      }

      onClose();
    };
    
    return (
      <li key={item.path}>
        <Link
          to={isLocked ? "#" : item.path}
          onClick={handleClick}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
          className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors ${
            isActive && !isLocked
              ? "bg-white/10 text-white"
              : isLocked
                ? "text-white/40 cursor-not-allowed"
                : "text-white/70 hover:bg-white/5 hover:text-white"
          }`}
        >
          <item.icon className={isTopLevel ? "w-5 h-5" : "w-4 h-4"} />
          <span className={isTopLevel ? "font-medium" : "text-sm"}>{item.label}</span>
          {/* Show unread message badge for Mesajlar/Mesajlarım */}
          {(item.label === "Mesajlar" || item.label === "Mesajlarım") && unreadMessageCount > 0 && (
            <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              +{unreadMessageCount}
            </span>
          )}
          {/* Show pending reservations badge for Rezervasyonlar */}
          {item.label === "Rezervasyonlar" && pendingReservationsCount > 0 && (
            <span className="ml-auto bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
              +{pendingReservationsCount}
            </span>
          )}
          {item.path === "/dashboard/members" && ((memberBadge?.total || 0) + pendingDemoCount) > 0 && (
            <span className="ml-auto bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full"
              title={`${pendingDemoCount} demo talebi, ${memberBadge?.package_requests || 0} paket talebi`}
              data-testid="sidebar-members-badge">
              +{ (memberBadge?.total || 0) + pendingDemoCount }
            </span>
          )}
          {/* Show admin pending badges (işletme onayları, adres talepleri) */}
          {item.badgeCount > 0 && (
            <span className="ml-auto bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
              +{item.badgeCount}
            </span>
          )}
          {isLocked && <Lock className="w-3 h-3 text-yellow-400 ml-auto" />}
        </Link>
      </li>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 bottom-0 w-64 bg-navy z-50 transform transition-transform duration-300 lg:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full pt-[env(safe-area-inset-top,0px)]">
          {/* Logo */}
          <div className="p-6 flex items-center justify-between relative">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_273f8795-754a-4865-8c8d-c4895c64dfec/artifacts/y1zkpgvm_Probody-Studio%20v2_01.png" 
                alt="Probody Studio Logo" 
                className="w-10 h-10 object-contain"
              />
              <span className="font-heading font-bold text-xl text-white">
                PBStudio
              </span>
            </Link>
            <button className="lg:hidden text-white/80 hover:text-white p-2 rounded-full bg-white/10" onClick={onClose}>
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User Info */}
          <div className="px-6 py-4 border-t border-white/10">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-white/10 text-white">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium text-white">{user?.name}</p>
                <p className="text-xs text-white/60">
                  {user?.role === "trainer" && user?.has_own_business 
                    ? `${t('common:trainer')} • ${t('common:businessOwner')}`
                    : user?.role === "trainer" 
                      ? t('common:trainer')
                      : user?.role === "business" 
                        ? t('common:businessOwner')
                        : user?.role === "employee"
                          ? t('employees:employee')
                          : user?.role === "member"
                            ? t('common:member')
                            : user?.role === "admin"
                              ? t('admin:admin')
                              : user?.role}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 sidebar-scroll overflow-y-auto">
            <ul className="space-y-1">
              {subscriptionFilteredGroups.map((group) => {
                const isExpanded = expandedGroups.includes(group.id);
                const hasActiveItem = group.items.some(item => location.pathname === item.path);
                
                // If no label, render items directly (single items) - these are top level
                if (!group.label) {
                  return group.items.map(item => renderMenuItem(item, onClose, true));
                }
                
                return (
                  <li key={group.id} className="mb-2">
                    {/* Group Header */}
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className={`flex items-center gap-3 w-full px-4 py-2.5 rounded-xl transition-colors ${
                        hasActiveItem 
                          ? "bg-white/10 text-white" 
                          : "text-white/70 hover:bg-white/5 hover:text-white"
                      }`}
                    >
                      <group.icon className="w-5 h-5" />
                      <span className="flex-1 text-left font-medium">{group.label}</span>
                      {!isExpanded && group.id === "uyeler" && memberBadge?.total > 0 && (
                        <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-testid="sidebar-members-group-badge"
                          title={`${memberBadge.package_requests} paket talebi, ${memberBadge.link_pending} onay bekleyen`}>{memberBadge.total}</span>
                      )}
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                    
                    {/* Group Items */}
                    {isExpanded && (
                      <ul className="mt-1 ml-4 pl-2 border-l border-white/10 space-y-1">
                        {group.items.map(item => renderMenuItem(item, onClose, false))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Language & Logout */}
          <div className="p-4 border-t border-white/10 space-y-2">
            <div className="flex items-center justify-center">
              <LanguageSwitcher variant="minimal" className="text-white/70 hover:text-white" />
            </div>
            <button
              onClick={onLogout}
              data-testid="logout-btn"
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-white/70 hover:bg-white/5 hover:text-white transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span>{t('common:logout')}</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Main Dashboard Page
export default function DashboardPage({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['dashboard', 'common', "reports", "packages", "employees", "admin"]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [currentUser, setCurrentUser] = useState(user || location.state?.user);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  
  // Pending reservations count for sidebar badge
  const [pendingReservationsCount, setPendingReservationsCount] = useState(0);
  const [memberBadge, setMemberBadge] = useState(null);
  const [groupClassesEnabled, setGroupClassesEnabled] = useState(false);
  const [pendingDemoCount, setPendingDemoCount] = useState(0);
  
  // Admin pending counts for sidebar badges
  const [pendingBusinessCount, setPendingBusinessCount] = useState(0);
  const [pendingAddressCount, setPendingAddressCount] = useState(0);
  const [pendingVisibilityCount, setPendingVisibilityCount] = useState(0);
  
  // User permissions for current business (for trainers and employees)
  const [userPermissions, setUserPermissions] = useState(null);
  
  // Employee businesses (for employees)
  const [employeeBusinesses, setEmployeeBusinesses] = useState([]);
  
  // Member memberships (for members)
  const [memberMemberships, setMemberMemberships] = useState([]);
  
  // Trainer-specific: businesses where user works as trainer
  const [trainerBusinesses, setTrainerBusinesses] = useState([]);
  const [selectedTrainerBusinessIndex, setSelectedTrainerBusinessIndex] = useState(() => {
    const saved = localStorage.getItem('selectedTrainerBusinessIndex');
    return saved ? parseInt(saved, 10) : 0;
  });
  
  // Selected business for multi-business support
  const [selectedBusinessIndex, setSelectedBusinessIndex] = useState(() => {
    const saved = localStorage.getItem('selectedBusinessIndex');
    return saved ? parseInt(saved, 10) : 0;
  });

  // Fetch fresh user data to ensure is_admin is loaded
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const response = await fetch(`${API}/auth/me`, {
          credentials: "include",
          headers,
        });
        if (response.ok) {
          const userData = await response.json();
          setCurrentUser(userData);
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
    fetchUserData();
  }, []);
  
  // Fetch unread message count function
  const fetchUnreadMessageCount = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API}/messages/unread/count`, {
        credentials: "include",
        headers,
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadMessageCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error("Error fetching unread message count:", error);
    }
  };
  
  // Fetch unread message count on mount and poll
  useEffect(() => {
    fetchUnreadMessageCount();
    // Poll every 30 seconds
    const interval = setInterval(fetchUnreadMessageCount, 30000);
    return () => clearInterval(interval);
  }, []);
  
  // Fetch admin pending counts (for admin users)
  const fetchAdminPendingCounts = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Fetch pending business approvals count
      const businessRes = await fetch(`${API}/admin/stats`, {
        credentials: "include",
        headers,
      });
      if (businessRes.ok) {
        const data = await businessRes.json();
        setPendingBusinessCount(data.pending_businesses || 0);
      }
      
      // Fetch pending address requests count
      const addressRes = await fetch(`${API}/admin/address-change-requests/stats`, {
        credentials: "include",
        headers,
      });
      if (addressRes.ok) {
        const data = await addressRes.json();
        setPendingAddressCount(data.pending || 0);
      }
      
      // Fetch trainer visibility requests count
      const visibilityRes = await fetch(`${API}/admin/trainer-visibility-stats`, {
        credentials: "include",
        headers,
      });
      if (visibilityRes.ok) {
        const data = await visibilityRes.json();
        setPendingVisibilityCount(data.pending || 0);
      }
    } catch (error) {
      console.error("Error fetching admin pending counts:", error);
    }
  };
  
  // Fetch admin counts when user is admin
  useEffect(() => {
    if (currentUser?.is_admin) {
      fetchAdminPendingCounts();
      // Poll every 60 seconds
      const interval = setInterval(fetchAdminPendingCounts, 60000);
      return () => clearInterval(interval);
    }
  }, [currentUser?.is_admin]);
  
  // Fetch trainer businesses (for trainers)
  const fetchTrainerBusinesses = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API}/me/trainer-businesses`, {
        credentials: "include",
        headers,
      });
      if (response.ok) {
        const data = await response.json();
        setTrainerBusinesses(data);
        
        // Reset selected index if out of bounds
        if (selectedTrainerBusinessIndex >= data.length && data.length > 0) {
          setSelectedTrainerBusinessIndex(0);
          localStorage.setItem('selectedTrainerBusinessIndex', '0');
        }
        
        // If trainer has businesses, fetch subscription for the selected one (if it's their own)
        const bizIndex = selectedTrainerBusinessIndex < data.length ? selectedTrainerBusinessIndex : 0;
        if (data.length > 0 && data[bizIndex].is_own_business) {
          const subRes = await fetch(`${API}/businesses/${data[bizIndex].business_id}/subscription`, {
            credentials: "include",
          });
          if (subRes.ok) {
            setSubscription(await subRes.json());
          } else {
            setSubscription(null); // No subscription for this business
          }
        } else if (data.length > 0 && !data[bizIndex].is_own_business) {
          // Not own business - fetch the business's subscription (for permission checks)
          const subRes = await fetch(`${API}/businesses/${data[bizIndex].business_id}/subscription`, {
            credentials: "include",
          });
          if (subRes.ok) {
            setSubscription(await subRes.json());
          } else {
            setSubscription(null);
          }
        } else {
          setSubscription(null);
        }
        
        // Fetch pending reservations count for sidebar badge
        if (data.length > 0) {
          fetchPendingReservationsCount(data[bizIndex].business_id);
        }
      }
    } catch (error) {
      console.error("Error fetching trainer businesses:", error);
    }
  };
  
  // Handle trainer business selection change
  const handleTrainerBusinessChange = (index) => {
    setSelectedTrainerBusinessIndex(index);
    localStorage.setItem('selectedTrainerBusinessIndex', index.toString());
    
    // Navigate to dashboard overview and reload to refresh all modules with new business data
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
      setTimeout(() => window.location.reload(), 50);
    } else {
      window.location.reload();
    }
  };
  
  // Get currently selected trainer business
  const selectedTrainerBusiness = trainerBusinesses[selectedTrainerBusinessIndex] || trainerBusinesses[0];

  // Fetch user permissions for current business
  const fetchUserPermissions = async (businessId) => {
    if (!businessId) return;
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API}/me/employee-permissions/${businessId}`, {
        credentials: "include",
        headers,
      });
      if (response.ok) {
        const data = await response.json();
        setUserPermissions(data);
        console.log("User permissions loaded:", data);
      }
    } catch (error) {
      console.error("Error fetching user permissions:", error);
    }
  };
  
  // Fetch employee businesses (for employees)
  const fetchEmployeeBusinesses = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API}/me/employee-businesses`, {
        credentials: "include",
        headers,
      });
      if (response.ok) {
        const data = await response.json();
        setEmployeeBusinesses(data);
        // Also set businesses for the dashboard display
        setBusinesses(data);
        
        // Fetch subscription for the employee's business (first one)
        if (data.length > 0) {
          const subRes = await fetch(`${API}/businesses/${data[0].business_id}/subscription`, {
            credentials: "include",
            headers,
          });
          if (subRes.ok) {
            setSubscription(await subRes.json());
          } else {
            setSubscription(null);
          }
          
          // Fetch pending reservations count for sidebar badge
          fetchPendingReservationsCount(data[0].business_id);
        }
      }
    } catch (error) {
      console.error("Error fetching employee businesses:", error);
    }
  };
  
  // Fetch member memberships (for members)
  const fetchMemberMemberships = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API}/members/by-user/${currentUser?.user_id}`, {
        credentials: "include",
        headers,
      });
      if (response.ok) {
        const data = await response.json();
        setMemberMemberships(data);
      }
    } catch (error) {
      console.error("Error fetching member memberships:", error);
    }
  };

  // Fetch pending reservations count for sidebar badge
  const fetchPendingReservationsCount = async (businessId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API}/reservations/tab-counts/${businessId}?viewer=business`, {
        credentials: "include",
        headers,
      });
      if (response.ok) {
        const data = await response.json();
        // requests + pending + changes = tüm bekleyen onaylar
        const total = (data.requests || 0) + (data.pending || 0) + (data.changes || 0);
        setPendingReservationsCount(total);
      }
    } catch (error) {
      console.error("Error fetching pending count:", error);
    }
  };

  // Demo Talepleri Bekleyen Sayısını Çeken Fonksiyon
  const fetchPendingDemoCount = async (businessId) => {
    if (!businessId) return;
    try {
      const res = await fetch(`${API}/reservations/demo-requests/${businessId}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setPendingDemoCount((data || []).filter((r) => r.status === "pending").length);
      }
    } catch (err) {}
  };

  const fetchMyBusinesses = async () => {
    try {
      const response = await fetch(`${API}/my-businesses`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setBusinesses(data);
        
        // Reset selected index if out of bounds
        if (selectedBusinessIndex >= data.length && data.length > 0) {
          setSelectedBusinessIndex(0);
          localStorage.setItem('selectedBusinessIndex', '0');
        }
        
        // Fetch subscription for selected business
        const bizIndex = selectedBusinessIndex < data.length ? selectedBusinessIndex : 0;
        if (data.length > 0) {
          const subRes = await fetch(`${API}/businesses/${data[bizIndex].business_id}/subscription`, {
            credentials: "include",
          });
          if (subRes.ok) {
            setSubscription(await subRes.json());
          } else {
            setSubscription(null); // No subscription
          }
          
          // Fetch pending reservations count for sidebar badge
          fetchPendingReservationsCount(data[bizIndex].business_id);
        }
      }
    } catch (error) {
      console.error("Error fetching businesses:", error);
    }
  };
  
  // Handle business selection change - reload page to refresh all modules
  const handleBusinessChange = async (index) => {
    setSelectedBusinessIndex(index);
    localStorage.setItem('selectedBusinessIndex', index.toString());
    
    // Navigate to dashboard overview and reload to refresh all modules with new business data
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
      // Small delay to ensure navigation completes before reload
      setTimeout(() => window.location.reload(), 50);
    } else {
      window.location.reload();
    }
  };
  
  // Get currently selected business
  const selectedBusiness = businesses[selectedBusinessIndex] || businesses[0];
  
  // Active business for current user (trainer, employee, member, or business owner)
  const activeBusinessId = currentUser?.role === "trainer" 
    ? selectedTrainerBusiness?.business_id 
    : currentUser?.role === "employee"
      ? employeeBusinesses[0]?.business_id
      : currentUser?.role === "member"
        ? memberMemberships[0]?.business_id
        : selectedBusiness?.business_id;
  
  const activeBusinessData = currentUser?.role === "trainer"
    ? selectedTrainerBusiness
    : currentUser?.role === "employee"
      ? employeeBusinesses[0]  // Employee's first business
      : currentUser?.role === "member"
        ? memberMemberships[0]
        : selectedBusiness;

  useEffect(() => {
    if (currentUser?.role === "business") {
      fetchMyBusinesses();
    } else if (currentUser?.role === "trainer") {
      fetchTrainerBusinesses();
    } else if (currentUser?.role === "employee") {
      fetchEmployeeBusinesses();
    } else if (currentUser?.role === "member") {
      fetchMemberMemberships();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);
  
  // Fetch permissions when business changes
  useEffect(() => {
    const businessId = activeBusinessId;
    if (businessId && (currentUser?.role === "trainer" || currentUser?.role === "employee")) {
      fetchUserPermissions(businessId);
    }
    // For business owners, set is_owner permission
    if (currentUser?.role === "business") {
      setUserPermissions({
        is_owner: true,
        is_manager: true,
        can_approve_reservations: true,
        permissions: {}
      });
    }
    // Fetch pending reservations count for sidebar badge
    if (businessId && currentUser?.role !== "member") {
      fetchPendingReservationsCount(businessId);
      fetchPendingDemoCount(businessId); // <--- İŞTE TAM BURAYA EKLİYORSUN
      fetch(`${API}/businesses/${businessId}/member-badges`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null)).then(setMemberBadge).catch(() => setMemberBadge(null));
    }
    if (currentUser?.role === "member") {
      fetch(`${API}/member/group-classes-enabled`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : { enabled: false })).then((d) => setGroupClassesEnabled(!!d.enabled)).catch(() => {});
    }
     
  }, [activeBusinessId, currentUser?.role]);

  // Demo Talebi arandı/iptal edildiğinde sol menüdeki sayıyı anında düşür
  useEffect(() => {
    const onUpdate = () => activeBusinessId && fetchPendingDemoCount(activeBusinessId);
    window.addEventListener("demoStatusUpdated", onUpdate);
    return () => window.removeEventListener("demoStatusUpdated", onUpdate);
  }, [activeBusinessId]);
  
  // Online rezervasyon talebi onaylandığında/reddedildiğinde sol menü rozetini anında düşür
  useEffect(() => {
    const refreshMemberBadges = () => {
      if (activeBusinessId) {
        fetch(`${API}/businesses/${activeBusinessId}/member-badges`, { credentials: "include" })
          .then((r) => (r.ok ? r.json() : null))
          .then(setMemberBadge)
          .catch(() => setMemberBadge(null));
      }
    };

    window.addEventListener("memberBadgesUpdated", refreshMemberBadges);
    return () => window.removeEventListener("memberBadgesUpdated", refreshMemberBadges);
  }, [activeBusinessId]);
  
  // Listen for subscription modules update event
  useEffect(() => {
    const handleSubscriptionUpdate = async () => {
      if (!activeBusinessId) return;
      
      try {
        const token = localStorage.getItem('auth_token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const subRes = await fetch(`${API}/businesses/${activeBusinessId}/subscription`, {
          credentials: "include",
          headers,
        });
        if (subRes.ok) {
          const data = await subRes.json();
          setSubscription(data);
        }
      } catch (error) {
        console.error("Error refreshing subscription:", error);
      }
    };
    
    window.addEventListener('subscription-modules-updated', handleSubscriptionUpdate);
    return () => window.removeEventListener('subscription-modules-updated', handleSubscriptionUpdate);
  }, [activeBusinessId]);

  // Route guard - redirect if trying to access restricted module (silently, no toast)
  useEffect(() => {
    if (!subscription || !location.pathname.startsWith('/dashboard/')) return;
    
    const currentPath = location.pathname;
    const pathToModuleMap = {
      "/dashboard/members": "members",
      "/dashboard/member-forms": "member_forms",
      "/dashboard/qr-scan": "qr_scan",
      "/dashboard/qr-checkin": "qr_scan",
      "/dashboard/measurements": "measurements",
      "/dashboard/reservations": "reservations",
      "/dashboard/payments": "payments",
      "/dashboard/salary-payments": "salary_payments",
      "/dashboard/bonus-payments": "bonus_payments",
      "/dashboard/business-expenses": "business_expenses",
      "/dashboard/trainer-collections": "trainer_collections",
      "/dashboard/commissions": "commissions",
      "/dashboard/reports": "reports",
      "/dashboard/rooms": "rooms",
      "/dashboard/packages": "packages",
      "/dashboard/all-packages": "all_packages",
      "/dashboard/price-list": "price_list",
      "/dashboard/trainers": "trainers",
      "/dashboard/employees": "employees",
      "/dashboard/branches": "branches",
      "/dashboard/analytics": "analytics",
      "/dashboard/surveys": "surveys",
      "/dashboard/events": "events",
      "/dashboard/evaluations": "evaluations",
      "/dashboard/goals": "goals",
      "/dashboard/messages": "messages",
      "/dashboard/ratings": "ratings",
    };
    
    // These paths should NEVER be restricted
    const neverRestrictPaths = ["/dashboard/settings", "/dashboard/subscription", "/dashboard"];
    if (neverRestrictPaths.includes(currentPath)) return;
    
    const moduleId = pathToModuleMap[currentPath];
    if (!moduleId) return; // Path doesn't require module check
    
    // Check if user has access
    const fullAccessPlans = ['pro', 'premium', 'starter', 'basic'];
    const hasFullAccess = subscription.is_active && fullAccessPlans.includes(subscription.plan);
    
    if (hasFullAccess) return; // Pro users have full access
    
    const moduleList = subscription.allowed_modules || subscription.features || [];
    const hasAccess = Array.isArray(moduleList) && (moduleList.includes('all') || moduleList.includes(moduleId));
    
    if (!hasAccess) {
      // Silently redirect to dashboard - no toast
      navigate("/dashboard");
    }
  }, [location.pathname, subscription, navigate]);

  // Check if feature/module is available based on subscription
  const hasFeature = (feature) => {
    if (!subscription) return false;
    
    // If subscription is active and plan is pro or higher, allow all features
    // Plans that unlock all features: pro, premium, starter, basic
    const fullAccessPlans = ['pro', 'premium', 'starter', 'basic'];
    if (subscription.is_active && fullAccessPlans.includes(subscription.plan)) {
      return true;
    }
    
    // Check if 'all' is in features/allowed_modules array (pro plan marker)
    const moduleList = subscription.allowed_modules || subscription.features || [];
    if (Array.isArray(moduleList) && moduleList.includes('all')) {
      return true;
    }
    
    // Check allowed_modules (new dynamic system) or features (legacy)
    if (Array.isArray(moduleList) && moduleList.length > 0) {
      return moduleList.includes(feature);
    }
    
    // features can be an object (with feature: true/false)
    if (typeof subscription.features === 'object' && subscription.features !== null && Object.keys(subscription.features).length > 0) {
      return subscription.features[feature] === true;
    }
    
    // Free plan - only basic features
    return false;
  };

  // Map menu paths to module IDs for dynamic access control
  const pathToModuleMap = {
    "/dashboard/members": "members",
    "/dashboard/member-forms": "member_forms",
    "/dashboard/qr-scan": "qr_scan",
    "/dashboard/qr-checkin": "qr_scan",
    "/dashboard/measurements": "measurements",
    "/dashboard/reservations": "reservations",
    "/dashboard/payments": "payments",
    "/dashboard/salary-payments": "salary_payments",
    "/dashboard/bonus-payments": "bonus_payments",
    "/dashboard/business-expenses": "business_expenses",
    "/dashboard/trainer-collections": "trainer_collections",
    "/dashboard/commissions": "commissions",
    "/dashboard/reports": "reports",
    "/dashboard/rooms": "rooms",
    "/dashboard/packages": "packages",
    "/dashboard/all-packages": "all_packages",
    "/dashboard/price-list": "price_list",
    "/dashboard/trainers": "trainers",
    "/dashboard/employees": "employees",
    "/dashboard/branches": "branches",
    "/dashboard/analytics": "analytics",
    "/dashboard/surveys": "surveys",
    "/dashboard/events": "events",
    "/dashboard/evaluations": "evaluations",
    "/dashboard/goals": "goals",
    "/dashboard/messages": "messages",
    "/dashboard/ratings": "ratings",
    "/dashboard/settings": "settings",
    "/dashboard/subscription": "subscription",
  };

  // Check if a menu item is accessible based on dynamic modules
  const hasModuleAccess = (path) => {
    const moduleId = pathToModuleMap[path];
    if (!moduleId) return true; // No module restriction for this path
    return hasFeature(moduleId);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      toast.success(t('dashboard:overview.loggedOut'), { duration: 2000 });
    } catch (error) {
      console.error("Logout error:", error);
    }
    
    // Clear all storage
    localStorage.removeItem('auth_token');
    sessionStorage.setItem('just_logged_out', 'true');
    
    // Force page reload to clear all cached state and redirect to landing
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar
        user={currentUser}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onLogout={handleLogout}
        subscription={subscription}
        trainerBusinesses={trainerBusinesses}
        selectedTrainerBusiness={selectedTrainerBusiness}
        onTrainerBusinessChange={handleTrainerBusinessChange}
        businesses={businesses}
        selectedBusiness={selectedBusiness}
        onBusinessChange={handleBusinessChange}
        userPermissions={userPermissions}
        employeeBusinesses={employeeBusinesses}
        activeBusinessId={activeBusinessId}
        unreadMessageCount={unreadMessageCount}
        pendingReservationsCount={pendingReservationsCount}
        memberBadge={memberBadge}
        groupClassesEnabled={groupClassesEnabled}
        pendingDemoCount={pendingDemoCount}
        pendingBusinessCount={pendingBusinessCount}
        pendingAddressCount={pendingAddressCount}
        pendingVisibilityCount={pendingVisibilityCount}
      />

      {/* Main Content */}
      <div className="lg:ml-64 min-h-screen flex flex-col">
        {/* Top Bar - Fixed on mobile */}
        <header className="bg-white border-b border-grey-200 sticky top-0 z-40" style={{position: '-webkit-sticky'}}>
          <div className="flex items-center justify-between px-4 lg:px-8 py-4 max-w-full overflow-x-hidden">
            <button
              className="lg:hidden p-2 hover:bg-slate-100 rounded-xl"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-sidebar-toggle"
            >
              <Menu className="w-6 h-6 text-navy" />
            </button>

            {/* Business Selector - show for business owners with multiple businesses */}
            {currentUser?.role === "business" && businesses.length > 0 && (
              <div className="flex items-center gap-2 ml-4">
                <Building2 className="w-5 h-5 text-navy" />
                <select
                  value={selectedBusinessIndex}
                  onChange={(e) => handleBusinessChange(parseInt(e.target.value))}
                  className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-medium text-navy focus:outline-none focus:ring-2 focus:ring-navy/20"
                  data-testid="business-selector"
                >
                  {businesses.map((biz, index) => (
                    <option key={biz.business_id} value={index}>
                      {biz.name} {biz.is_branch ? t('dashboard:overview.branchSuffix') : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Trainer Business Selector - show for trainers */}
            {currentUser?.role === "trainer" && trainerBusinesses && trainerBusinesses.length > 0 && (
              <div className="flex items-center gap-2 ml-4">
                <Building2 className="w-5 h-5 text-navy" />
                <select
                  value={trainerBusinesses.findIndex(b => b.business_id === selectedTrainerBusiness?.business_id)}
                  onChange={(e) => handleTrainerBusinessChange(parseInt(e.target.value))}
                  className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-medium text-navy focus:outline-none focus:ring-2 focus:ring-navy/20"
                  data-testid="trainer-business-selector"
                >
                  {trainerBusinesses.map((biz, idx) => (
                    <option key={biz.business_id} value={idx}>
                      {biz.name} {biz.is_own_business ? t('dashboard:overview.ownSuffix') : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex-1 min-w-0" />

            <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
              <NotificationBell user={currentUser} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="profile-dropdown-trigger">
                    <Avatar className="h-9 w-9 cursor-pointer">
                      <AvatarImage src={currentUser?.picture} />
                      <AvatarFallback className="bg-navy text-white text-sm">
                        {currentUser?.name?.charAt(0) || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <ChevronDown className="w-4 h-4 text-grey hidden sm:block" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{currentUser?.name}</p>
                      <p className="text-xs text-grey">{currentUser?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="cursor-pointer"
                    onClick={() => navigate("/dashboard/profile")}
                    data-testid="profile-menu-item"
                  >
                    <User className="w-4 h-4 mr-2" />
                    {t('dashboard:sidebar.myProfile')}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="cursor-pointer"
                    onClick={() => navigate("/dashboard/settings")}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    {t('dashboard:overview.settingsLabel')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="cursor-pointer text-red-600 focus:text-red-600"
                    onClick={handleLogout}
                    data-testid="logout-menu-item"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('dashboard:overview.logoutLabel')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8 flex-1 overflow-x-hidden">
          <Routes>
            <Route
              index
              element={
                <DashboardOverview 
                  user={currentUser} 
                  businesses={currentUser?.role === "trainer" ? trainerBusinesses : businesses} 
                  selectedBusiness={activeBusinessData}
                  userPermissions={userPermissions}
                />
              }
            />
            <Route
              path="requests"
              element={
                activeBusinessData ? (
                  <RequestsModule businessId={activeBusinessId} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="reservations"
              element={
                activeBusinessData ? (
                  hasFeature("reservations") || currentUser?.role === "trainer" ? (
                    <ReservationsModule 
                      businessId={activeBusinessId} 
                      subscription={subscription} 
                      userPermissions={userPermissions}
                      currentUser={currentUser}
                    />
                  ) : (
                    <UpgradePrompt 
                      feature={t("common:reservationSystem")} 
                      requiredPlan="pro" 
                      businessId={activeBusinessId}
                    />
                  )
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="members"
              element={
                activeBusinessData ? (
                  hasFeature("members") || currentUser?.role === "trainer" ? (
                    <MembersModule 
                      businessId={activeBusinessId} 
                      subscription={subscription}
                      user={user}
                      businessOwnerId={activeBusinessData?.owner_id}
                    />
                  ) : (
                    <UpgradePrompt 
                      feature={t("common:memberManagement")} 
                      requiredPlan="pro" 
                      businessId={activeBusinessId}
                    />
                  )
                ) : (
                  <div className="text-center py-16 text-grey">
                    Önce bir işletme eklemeniz gerekiyor
                  </div>
                )
              }
            />
            <Route
              path="members/:memberId"
              element={
                activeBusinessData ? (
                  <MemberDetailPage businessId={activeBusinessId} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    Önce bir işletme eklemeniz gerekiyor
                  </div>
                )
              }
            />
            <Route
              path="trainers/:trainerId"
              element={
                activeBusinessData ? (
                  <TrainerDetailPage businessId={activeBusinessId} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    Önce bir işletme eklemeniz gerekiyor
                  </div>
                )
              }
            />
            <Route
              path="employees/:employeeId"
              element={
                activeBusinessData ? (
                  <EmployeeDetailPage businessId={activeBusinessId} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    Önce bir işletme eklemeniz gerekiyor
                  </div>
                )
              }
            />
            <Route
              path="all-packages"
              element={
                activeBusinessData ? (
                  hasFeature("members") || activeBusinessData?.is_own_business || userPermissions?.is_manager || userPermissions?.is_co_owner ? (
                    <AllPackagesModule businessId={activeBusinessId} />
                  ) : (
                    <UpgradePrompt 
                      feature={t("packages:allPackages")} 
                      requiredPlan="pro" 
                      businessId={activeBusinessId}
                    />
                  )
                ) : (
                  <div className="text-center py-16 text-grey">
                    Önce bir işletme eklemeniz gerekiyor
                  </div>
                )
              }
            />
            <Route
              path="trainers"
              element={
                currentUser?.role === "trainer" && !selectedTrainerBusiness?.is_own_business && !userPermissions?.is_manager && !userPermissions?.is_co_owner ? (
                  <div className="text-center py-16 text-grey">
                    <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-red-500" />
                    <p className="font-medium">{t("employees:noAccessPermission")}</p>
                    <p className="text-sm mt-2">{t("employees:needManagerAccess")}</p>
                  </div>
                ) : activeBusinessData ? (
                  hasFeature("trainers") || activeBusinessData?.is_own_business || userPermissions?.is_manager ? (
                    <TrainersModule businessId={activeBusinessId} subscription={subscription} />
                  ) : (
                    <UpgradePrompt 
                      feature={t("common:trainerManagement")} 
                      requiredPlan="pro" 
                      businessId={activeBusinessId}
                    />
                  )
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="audit-log"
              element={
                (currentUser?.role === "business" || 
                selectedTrainerBusiness?.is_own_business || 
                activeBusinessData?.is_own_business || 
                activeBusinessData?.owner_id === currentUser?.user_id || 
                userPermissions?.is_owner) ? (
                  <AuditLogModule businessId={activeBusinessId} />
                ) : (
                  <div className="text-center py-16 text-grey">Denetim kaydını yalnızca işletme sahibi görebilir</div>
                )
              }
            />
            <Route
              path="employees"
              element={
                activeBusinessData ? (
                  <EmployeesModule businessId={activeBusinessId} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    Önce bir işletme eklemeniz gerekiyor
                  </div>
                )
              }
            />
            <Route
              path="rooms"
              element={
                currentUser?.role === "trainer" && !selectedTrainerBusiness?.is_own_business && !userPermissions?.is_manager && !userPermissions?.is_co_owner ? (
                  <div className="text-center py-16 text-grey">
                    <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-red-500" />
                    <p className="font-medium">{t("employees:noAccessPermission")}</p>
                    <p className="text-sm mt-2">{t("employees:needManagerAccess")}</p>
                  </div>
                ) : activeBusinessData ? (
                  <RoomsModule businessId={activeBusinessId} subscription={subscription} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="packages"
              element={
                currentUser?.role === "trainer" && !selectedTrainerBusiness?.is_own_business && !userPermissions?.is_manager && !userPermissions?.is_co_owner ? (
                  <div className="text-center py-16 text-grey">
                    <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-red-500" />
                    <p className="font-medium">{t("employees:noAccessPermission")}</p>
                    <p className="text-sm mt-2">{t("employees:needManagerAccess")}</p>
                  </div>
                ) : activeBusinessData ? (
                  <PackagesModule businessId={activeBusinessId} subscription={subscription} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="price-list"
              element={
                currentUser?.role === "trainer" && !selectedTrainerBusiness?.is_own_business && !userPermissions?.is_manager && !userPermissions?.is_co_owner ? (
                  <div className="text-center py-16 text-grey">
                    <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-red-500" />
                    <p className="font-medium">{t("employees:noAccessPermission")}</p>
                    <p className="text-sm mt-2">{t("employees:needManagerAccess")}</p>
                  </div>
                ) : activeBusinessData ? (
                  <PriceListModule businessId={activeBusinessId} subscription={subscription} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="payments"
              element={
                activeBusinessData ? (
                  <PaymentsModule businessId={activeBusinessId} subscription={subscription} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="measurements"
              element={
                activeBusinessData ? (
                  <MeasurementsModule businessId={activeBusinessId} subscription={subscription} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="messages"
              element={<MessagingModule user={currentUser} businessId={activeBusinessId} onUnreadCountChange={fetchUnreadMessageCount} />}
            />
            <Route
              path="member-forms"
              element={
                activeBusinessData ? (
                  <MemberFormsModule businessId={activeBusinessId} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="surveys"
              element={
                activeBusinessData ? (
                  <BusinessSurveysModule businessId={activeBusinessId} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="events"
              element={
                activeBusinessData ? (
                  <EventsModule businessId={activeBusinessId} trainers={activeBusinessData?.trainers || []} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="evaluations"
              element={
                activeBusinessData ? (
                  <EvaluationsModule businessId={activeBusinessId} user={currentUser} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="goals"
              element={
                activeBusinessData ? (
                  <GoalsModule businessId={activeBusinessId} user={currentUser} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="qr-scan"
              element={
                activeBusinessData ? (
                  <QRScanModule businessId={activeBusinessId} subscription={subscription} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="subscription"
              element={
                !activeBusinessData ? (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                ) : (currentUser?.role === "business" || userPermissions?.is_owner || userPermissions?.is_co_owner) ? (
                  <SubscriptionModule 
                    businessId={activeBusinessId} 
                    onPlanActivated={() => {
                      const fetchSub = async () => {
                        try {
                          const response = await fetch(`${API}/businesses/${activeBusinessId}/subscription`, {
                            credentials: "include"
                          });
                          if (response.ok) {
                            const data = await response.json();
                            setSubscription(data);
                          }
                        } catch (error) {
                          console.error("Error refreshing subscription:", error);
                        }
                      };
                      fetchSub();
                    }}
                  />
                ) : (
                  <div className="text-center py-16 text-grey">
                    <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-red-500" />
                    <p className="font-medium">{t("employees:noAccessPermission")}</p>
                    <p className="text-sm mt-2">Abonelik yönetimini sadece işletme sahibi yapabilir.</p>
                  </div>
                )
              }
            />
            <Route
              path="admin-codes"
              element={<AdminCodeGeneratorModule />}
            />
            <Route
              path="admin-settings"
              element={<AdminSettingsModule />}
            />
            <Route
              path="business-approvals"
              element={<BusinessApprovalModule onBadgeUpdate={fetchAdminPendingCounts} />}
            />
            <Route
              path="address-requests"
              element={<AddressChangeRequestsModule onBadgeUpdate={fetchAdminPendingCounts} />}
            />
            <Route
              path="trainer-visibility"
              element={<TrainerVisibilityRequestsModule onUpdate={fetchAdminPendingCounts} />}
            />
            <Route
              path="reset-db"
              element={<ResetDatabaseModule />}
            />
            <Route
              path="translations"
              element={<TranslationManagementModule />}
            />
            <Route
              path="analytics"
              element={
                activeBusinessData ? (
                  hasFeature("analytics") || activeBusinessData?.is_own_business ? (
                    <AnalyticsModule businessId={activeBusinessId} />
                  ) : (
                    <UpgradePrompt 
                      feature={t("reports:analyticsAndReports")} 
                      requiredPlan="pro" 
                      businessId={activeBusinessId}
                    />
                  )
                ) : (
                  <div className="text-center py-16 text-grey">
                    Önce bir işletme eklemeniz gerekiyor
                  </div>
                )
              }
            />
            <Route
              path="commissions"
              element={
                activeBusinessData ? (
                  <CommissionsModule 
                    businessId={activeBusinessId} 
                    isTrainerView={currentUser?.role === "trainer" && !selectedTrainerBusiness?.is_own_business && !userPermissions?.is_manager && !userPermissions?.is_co_owner}
                    currentTrainerId={selectedTrainerBusiness?.trainer_id}
                  />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="salary-payments"
              element={
                activeBusinessData ? (
                  <SalaryPaymentsModule businessId={activeBusinessId} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="bonus-payments"
              element={
                activeBusinessData ? (
                  <BonusPaymentsModule businessId={activeBusinessId} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="trainer-collections"
              element={
                activeBusinessData ? (
                  <TrainerCollectionsModule businessId={activeBusinessId} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="business-expenses"
              element={
                activeBusinessData ? (
                  <BusinessExpensesModule businessId={activeBusinessId} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="reports"
              element={
                activeBusinessData ? (
                  <ReportsModule businessId={activeBusinessId} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="branches"
              element={
                currentUser?.role === "trainer" && !selectedTrainerBusiness?.is_own_business && !userPermissions?.is_manager && !userPermissions?.is_co_owner ? (
                  <div className="text-center py-16 text-grey">
                    <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-red-500" />
                    <p className="font-medium">{t("employees:noAccessPermission")}</p>
                    <p className="text-sm mt-2">{t("employees:needManagerAccess")}</p>
                  </div>
                ) : activeBusinessData ? (
                  <BranchManagement businessId={activeBusinessId} business={activeBusinessData} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="ratings"
              element={
                activeBusinessData ? (
                  <RatingModule 
                    user={currentUser}
                    targetType="business" 
                    targetId={activeBusinessData?.business_id}
                    targetName={activeBusinessData?.name}
                  />
                ) : (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                )
              }
            />
            <Route
              path="settings"
              element={
                !activeBusinessData ? (
                  <div className="text-center py-16 text-grey">
                    {currentUser?.role === "trainer" ? t("common:trainerNoBusinessFound") : t("common:needToAddBusiness")}
                  </div>
                ) : (
                  // İşletme sahibi, kendi işletmesini yöneten antrenör veya şirket ortağı her zaman görür
                  currentUser?.role === "business" || 
                  selectedTrainerBusiness?.is_own_business || 
                  userPermissions?.is_owner || 
                  userPermissions?.is_co_owner || 
                  // Eğer yönetici çalışan/antrenör ise: ayarlar izinlerinden en az biri açık olmalı
                  (userPermissions?.is_manager && (
                    userPermissions?.permissions?.settings === true ||
                    userPermissions?.permissions?.settings_business_info === true ||
                    userPermissions?.permissions?.settings_pricing === true ||
                    userPermissions?.permissions?.settings_working_hours === true ||
                    userPermissions?.permissions?.settings_branches === true ||
                    userPermissions?.permissions?.settings_training_types === true ||
                    userPermissions?.permissions?.settings_measurements === true ||
                    userPermissions?.permissions?.settings_expense_types === true ||
                    userPermissions?.permissions?.settings_numbering === true ||
                    userPermissions?.permissions?.settings_currency === true
                  ))
                ) ? (
                  <SettingsModule 
                    businessId={activeBusinessId} 
                    business={activeBusinessData}
                    onUpdate={currentUser?.role === "trainer" ? fetchTrainerBusinesses : fetchMyBusinesses}
                    user={currentUser}
                    isOwnBusiness={currentUser?.role === "business" || selectedTrainerBusiness?.is_own_business || false}
                    userPermissions={userPermissions}
                    subscription={subscription}
                  />
                ) : (
                  <div className="text-center py-16 text-grey">
                    <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-red-500" />
                    <p className="font-medium">{t("employees:noAccessPermission")}</p>
                    <p className="text-sm mt-2">{t("employees:needManagerAccess")}</p>
                  </div>
                )
              }
            />
            {/* Member Routes */}
            <Route
              path="my-reservations"
              element={
                <MemberReservationCalendar 
                  user={currentUser} 
                  memberPackage={null}
                />
              }
            />
            <Route
              path="create-request"
              element={<MemberCreateRequest />}
            />
            <Route path="group-classes" element={<MemberGroupClasses />} />
            <Route
              path="qr-checkin"
              element={<MemberQRCheckin />}
            />
            <Route
              path="my-packages"
              element={<MyPackagesModule user={currentUser} />}
            />
            <Route
              path="my-payments"
              element={<PaymentsModule businessId={activeBusinessId} subscription={subscription} memberView={true} user={currentUser} />}
            />
            <Route
              path="my-measurements"
              element={<MyMeasurementsModule user={currentUser} />}
            />
            <Route
              path="my-surveys"
              element={<MySurveysModule user={currentUser} businessId={activeBusinessId} />}
            />
            <Route
              path="my-events"
              element={<MyEventsModule user={currentUser} businessId={activeBusinessId} />}
            />
            <Route
              path="my-evaluations"
              element={<MyEvaluationsModule user={currentUser} businessId={activeBusinessId} />}
            />
            {/* Trainer Routes */}
            <Route
              path="my-classes"
              element={<MyClassesModule user={currentUser} businessId={activeBusinessId} selectedBusiness={selectedTrainerBusiness} />}
            />
            <Route
              path="my-students"
              element={
                activeBusinessData ? (
                  <MyStudentsModule user={currentUser} businessId={activeBusinessId} />
                ) : (
                  <div className="text-center py-16 text-grey">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>{t("common:calistiginizBirIsletmeBulunamadi")}</p>
                  </div>
                )
              }
            />
            <Route
              path="my-commissions"
              element={<MyCommissionsModule user={currentUser} />}
            />
            <Route
              path="my-finances"
              element={<MyFinancesModule user={currentUser} />}
            />
            {/* Trainer Extended Routes */}
            <Route
              path="trainer-sell-package"
              element={<TrainerSellPackageModule user={currentUser} />}
            />
            <Route
              path="trainer-payments"
              element={<TrainerPaymentsModule user={currentUser} />}
            />
            <Route
              path="trainer-reservations"
              element={<TrainerReservationsModule user={currentUser} />}
            />
            {/* User Profile Route - for all roles */}
            <Route
              path="profile"
              element={<UserProfileModule user={currentUser} onUpdate={() => window.location.reload()} />}
            />
            {/* Member Requests Tracking Route */}
            <Route
              path="my-requests"
              element={<MemberRequestsModule />}
            />
          </Routes>
        </main>
        
        {/* Footer */}
        <footer className="mt-auto py-4 px-6 border-t border-gray-200 bg-white">
          <div className="text-center text-sm text-gray-500">
            © 2026 PBStudio - Probody Systems
          </div>
        </footer>
      </div>
    </div>
  );
}
